import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import { useSiteContent } from '../../context/SiteContentContext';
import { useContentfulCollection } from '../../hooks/useContentfulCollection';
import { useContentfulAssets } from '../../hooks/useContentfulAssets';
import { ScrollTrackerProvider, useScrollTracker } from '../../hooks/ScrollTrackerContext';
import { BlurFade } from '../ui/BlurFade';
import { DeploymentWorldMap } from '../ui/WorldMap';
import { brandLogoGreenAsset, brandLogoLightAsset, rivelinBackgroundGraphicAsset } from '../../lib/siteAssets';
import { LATEST_SECTION_ID, normalizeSiteHref } from '../../lib/siteRoutes';
import { CountingNumber } from './CountingNumber';
import { LazyVideo } from './LazyVideo';
import { OptimizedImage } from './OptimizedImage';
import { TeamCredential } from './TeamCredential';

const HOME_MEDIA_CDN_BASE_URL = (process.env.REACT_APP_MEDIA_CDN_BASE_URL || 'https://d8xhyovu00185.cloudfront.net').replace(/\/$/, '');
const HOME_LATEST_SOURCE_TITLE = 'Production language, not brochure language.';
const HOME_R1000_SOURCE_EYEBROWS = [
  'r1000 platform',
  'SATISFIED CLIENTS',
  'Satisfied clients',
  'The r1000',
];
const HOME_R1000_SOURCE_TITLES = [
  'A compact microfactory with a clearer deployment story.',
  'Unlock unparalleled efficiency with the r1000',
  'Join the happy clients already benefiting from the r1000',
];
const HOME_R1000_RENDER_TITLE = 'Meet the r1000';
const HOME_R1000_RENDER_BODY = 'Adaptive scanning, toolpath control, and finishing intelligence in one compact production microfactory.';
const TECHNOLOGY_FLOW_TITLE = 'A three-stage system rather than a disconnected toolchain.';
const TECHNOLOGY_PLATFORM_FEATURES_TITLE = 'What makes the system operationally different.';
const TECHNOLOGY_WHY_IT_MATTERS_TITLE = 'Technical sophistication only matters if it reduces operational friction.';
const COMPANY_MODEL_SECTION_TITLE = 'Investors and partners need more than robotics capability.';
const COMPANY_TIMELINE_SECTION_TITLE = 'Why the company exists and where it is headed.';
const SOLUTION_AREAS_SECTION = {
  type: 'cards',
  title: 'Four workflows. One microfactory.',
  columns: 2,
  showNumbers: true,
  items: [
    { title: 'Support removal', body: 'Repeatable bulk finishing across powder bed, wire arc, binder jet, cold spray, casting, forging, MRO.' },
    { title: 'Targeted finishing', body: 'Force-aware detail finishing for difficult geometries and mission-critical surfaces.' },
    { title: 'Platform removal', body: 'A safer, better-instrumented alternative to manual, bandsaw, EDM, CNC.' },
    { title: 'Cleaning', body: 'Adaptive handling for residual powder in internal passages and channels.' },
  ],
};

const HOME_PROBLEM_SECTION = {
  type: 'cards',
  theme: 'soft',
  eyebrow: 'The problem',
  title: 'Production makes near-net shape parts. Post-processing creates the bottleneck.',
  body: 'Support / gate removal, deburring, surface finishing, cleaning and platform removal are still largely manual: inconsistent, slow, and hard to validate at scale.',
  items: [
    {
      title: 'Hand finishing is a bottleneck and a hazard',
      body: 'Manual de-platforming and finishing expose operators to HSE risk while creating a production chokepoint that limits throughput and capacity planning.',
    },
    {
      title: 'Quality master craftsman are hard to clone',
      body: 'It takes decades to become a master craftsman. The skill and tenacity to stick with one of the toughest jobs there is, means they’re being paid to come out of retirement. This critical shortage of skilled people is having a major impact on supply chain resilience.',
    },
    {
      title: 'Today\'s methods add cost and delay certification',
      body: 'Manual post-processing inflates unit cost, extends lead times, and makes process validation harder, slowing the path to production sign-off.',
    },
  ],
};

const HOME_TECHNOLOGY_FLOW_SECTION = {
  type: 'process',
  theme: 'soft',
  eyebrow: 'The product',
  title: 'Rivelin automates the finishing step.',
  body: 'NetShape® scans each part, adapts to its real geometry, and drives finishing with robotic precision. The same intelligence runs across every Rivelin platform.',
  stepsEyebrow: 'How it works',
  equation: {
    inputs: ['8-axis Robot', 'Hand Tools', 'AI Programmer'],
    outputs: ['Feel', 'Touch', 'Dexterity', '±0.05mm repeatability', '±0.25mm accuracy'],
  },
  items: [
    {
      title: 'Scan',
      body: 'Capture each part and establish the real geometry before committing to a finishing path.',
    },
    {
      title: 'Adapt',
      body: 'Use intelligent planning to respond to variability in placement, geometry, and condition.',
    },
    {
      title: 'Finish',
      body: 'Apply finishing-aware control with standard industrial tools, but with robotic repeatability and traceability.',
    },
  ],
};
const HOME_SIGNALS_TITLES = ['Less copy. Clearer signals.', 'Reduce the burden of Post-Processing'];
const HOME_CASE_STUDIES_PREVIEW_TITLE = 'Minimal statements. Real outcomes.';
const HOME_NEXT_ROUTES_TITLE = 'Investor clarity. Jobseeker clarity.';
const HOME_R1000_RENDER_MEDIA_SRC = `${HOME_MEDIA_CDN_BASE_URL}/r1000.png`;
const HOME_R1000_RENDER_LOADING_LOGO = brandLogoGreenAsset;
const R1000_PRODUCT_VIDEO_SRC = `${HOME_MEDIA_CDN_BASE_URL}/Riv_Website-r1000Video_2026-05-12.mp4`;
const R1000_PRODUCT_VIDEO_TITLE = 'See the r1000 in motion.';
const R1000_PRODUCT_VIDEO_SECTION = {
  type: 'mediaSpotlight',
  eyebrow: 'Product video',
  title: R1000_PRODUCT_VIDEO_TITLE,
  body:
    'The current product video shows how the r1000 brings scanning, path planning, tooling, and finishing into one production-minded workflow.',
  media: {
    type: 'video',
    src: R1000_PRODUCT_VIDEO_SRC,
    poster: HOME_R1000_RENDER_MEDIA_SRC,
    alt: 'r1000 finishing and deployment sequence.',
  },
  actions: [
    { label: 'Book a demo', href: '/request-demo' },
  ],
  pills: ['Scan', 'Plan', 'Finish', 'Reset'],
  invert: true,
};
const R1000_TECHNOLOGY_VIDEO_SRC = `${HOME_MEDIA_CDN_BASE_URL}/Riv_r1000-Slideshow_2026-01-10_vLD.mp4`;
const R1000_TECHNOLOGY_VIDEO_SECTION = {
  type: 'mediaSpotlight',
  eyebrow: 'Why it matters',
  title: TECHNOLOGY_WHY_IT_MATTERS_TITLE,
  body:
    'Force control, adaptive planning, and deployment support reduce risk, waste, and uncertainty on the factory floor.',
  media: {
    type: 'video',
    src: R1000_TECHNOLOGY_VIDEO_SRC,
    poster: HOME_R1000_RENDER_MEDIA_SRC,
    alt: 'r1000 slideshow sequence.',
  },
  mediaAspectRatio: 'pinterest',
  actions: [{ label: 'See the company story', href: '/company' }],
};
const R500_RENDER_MEDIA_SRC = '/r500/r500-render.png';
const ROBOTIC_ROOF_MODULE_MEDIA_SRC = `${HOME_MEDIA_CDN_BASE_URL}/Slider-Bilder_AT800_Robotic.png`;
const HERO_CENTER_LOGO = brandLogoLightAsset;
const RIVELIN_BACKGROUND_GRAPHIC = rivelinBackgroundGraphicAsset;
const SOLUTIONS_EXPLORE_HERO = {
  eyebrow: 'Explore products',
  title: 'Explore products',
  body: 'Choose the Rivelin product route that fits your production stage.',
  primaryAction: { label: 'Explore r1000', href: '/products/r1000' },
  secondaryAction: { label: 'Request Early Access', href: '/products/r500' },
  media: { type: 'image', src: HOME_R1000_RENDER_MEDIA_SRC, alt: '3D render of the r1000 microfactory.' },
};
const SOLUTIONS_PRODUCT_CHOOSER_SECTION = {
  type: 'productChooser',
  anchorId: 'product-routes',
  eyebrow: 'Products',
  items: [
    {
      eyebrow: 'Available product',
      title: 'r1000',
      body: 'Adaptive scanning, toolpath control, and finishing intelligence in one compact production microfactory.',
      href: '/products/r1000',
      actionLabel: 'Explore r1000',
      media: {
        type: 'image',
        src: HOME_R1000_RENDER_MEDIA_SRC,
        alt: '3D render of the r1000 microfactory.',
      },
      tags: ['Production microfactory', 'Adaptive finishing'],
    },
    {
      eyebrow: 'Upcoming product',
      title: 'r500',
      body: 'The compact route. Same NetShape® intelligence in a smaller footprint.',
      href: '/solutions/r500',
      actionLabel: 'Request Early Access',
      media: {
        type: 'image',
        src: R500_RENDER_MEDIA_SRC,
        alt: '3D render of the r500 microfactory.',
      },
      tags: ['Waitlist route', 'Early access'],
    },
    {
      eyebrow: 'Upcoming product',
      title: 'rAD-AT800S',
      body: 'Robot Assisted Depowdering for the Solukon AT-800-S.\nAutomated powder removal and robotic finishing in one integrated system.',
      href: '/products/robotic-roof-module',
      actionLabel: 'Request Early Access',
      mediaFit: 'cover',
      media: {
        type: 'image',
        src: ROBOTIC_ROOF_MODULE_MEDIA_SRC,
        alt: 'rAD-AT800S integrated into the Solukon SFM-AT800-S.',
      },
      tags: ['Waitlist route', 'Early access'],
    },
  ],
};
const HERO_COMPANY_LOGOS = [
  { name: '3T Additive Manufacturing', file: '3t-am.webp' },
  { name: 'Acequia', file: 'acequia.webp' },
  { name: 'Attenborough Medical', file: 'attenborough-medical.png' },
  { name: 'BRL', file: 'brl.png' },
  { name: 'Creative Destruction Lab', file: 'creative-destruction-lab.png' },
  { name: 'DASA', file: 'dasa.png' },
  { id: 'confidential-divergent', name: 'Confidential', text: 'Confidential' },
  { name: 'GE Aerospace', file: 'ge-aerospace.png' },
  { name: 'Innovate UK', file: 'innovate-uk.png' },
  { name: 'Royal Air Force', file: 'raf.png' },
  { name: 'NRC CNRC', file: 'nrc-cnrc.png' },
  { name: 'Oerlikon', file: 'oerlikon.png' },
  { name: 'GKN', file: 'gkn.png' },
  { name: 'Renishaw', file: 'renishaw.png' },
  { id: 'confidential-rolls-royce', name: 'Confidential', text: 'Confidential' },
  { name: 'Siemens Energy', file: 'siemens-energy.png' },
  { name: 'Solukon', file: 'solukon.png' },
  { name: 'Trinity', file: 'trinity.png' },
];
const DEPLOYMENT_REGIONS = [
  { key: 'NA', label: 'North America', color: '#174f70' },
  { key: 'EMEA', label: 'EMEA', color: '#309e56' },
  { key: 'APAC', label: 'APAC', color: '#7d5cc8' },
];
const DEPLOYMENT_YEAR_COLORS = ['#174f70', '#F7D00D', '#309e56', '#7d5cc8'];
const DEPLOYMENT_YEAR_BG_COLORS = ['#f0f7ff', '#fffaf2', '#f2fbf5', '#f9f4fd'];
const DEPLOYMENT_YEAR_MARKERS = [
  // FYE 2026 - 7 dots (4 NA, 3 EMEA)
  [
    { lat: 39.5, lng: -98.5 }, { lat: 43.7, lng: -79.4 }, { lat: 37.0, lng: -120.5 }, { lat: 40.5, lng: -74.5 },
    { lat: 52.5, lng: -1.5 }, { lat: 51.5, lng: -0.1 }, { lat: 48.8, lng: 2.3 },
  ],
  // FYE 2027 - 15 dots (11 NA, 4 EMEA)
  [
    { lat: 45.0, lng: -93.0 }, { lat: 33.0, lng: -97.0 }, { lat: 42.0, lng: -87.5 },
    { lat: 34.0, lng: -118.0 }, { lat: 29.7, lng: -95.4 }, { lat: 38.9, lng: -77.0 },
    { lat: 47.6, lng: -122.3 }, { lat: 32.7, lng: -96.8 }, { lat: 41.8, lng: -71.4 },
    { lat: 51.0, lng: -114.0 }, { lat: 45.5, lng: -73.6 },
    { lat: 51.2, lng: 10.5 }, { lat: 47.4, lng: 8.5 }, { lat: 48.8, lng: 2.3 },
    { lat: 53.3, lng: -6.3 },
  ],
  // FYE 2028 - 55 dots (31 NA, 22 EMEA, 2 APAC)
  [
    { lat: 36.2, lng: -86.8 }, { lat: 39.1, lng: -84.5 }, { lat: 44.0, lng: -123.1 },
    { lat: 35.2, lng: -80.8 }, { lat: 30.3, lng: -81.7 }, { lat: 41.5, lng: -81.7 },
    { lat: 33.4, lng: -112.0 }, { lat: 39.7, lng: -105.0 }, { lat: 38.6, lng: -90.2 },
    { lat: 43.0, lng: -83.7 }, { lat: 44.9, lng: -93.3 }, { lat: 36.8, lng: -76.0 },
    { lat: 40.8, lng: -73.2 }, { lat: 42.4, lng: -71.1 }, { lat: 37.5, lng: -77.4 },
    { lat: 35.8, lng: -78.6 }, { lat: 46.9, lng: -71.2 }, { lat: 49.3, lng: -123.1 },
    { lat: 53.5, lng: -113.5 }, { lat: 43.6, lng: -79.4 }, { lat: 31.2, lng: -85.4 },
    { lat: 30.4, lng: -97.7 }, { lat: 36.1, lng: -115.2 }, { lat: 41.3, lng: -96.0 },
    { lat: 39.8, lng: -86.2 }, { lat: 35.1, lng: -106.6 }, { lat: 47.0, lng: -93.5 },
    { lat: 44.3, lng: -72.6 }, { lat: 42.9, lng: -78.9 }, { lat: 40.0, lng: -82.0 },
    { lat: 33.8, lng: -84.4 },
    { lat: 45.5, lng: 9.2 }, { lat: 40.4, lng: -3.7 }, { lat: 59.3, lng: 18.1 },
    { lat: 55.7, lng: 12.6 }, { lat: 50.1, lng: 14.4 }, { lat: 52.4, lng: 4.9 },
    { lat: 50.8, lng: 4.4 }, { lat: 48.1, lng: 11.6 }, { lat: 46.2, lng: 6.1 },
    { lat: 60.2, lng: 24.9 }, { lat: 52.2, lng: 0.1 }, { lat: 43.3, lng: -1.9 },
    { lat: 41.9, lng: 12.5 }, { lat: 47.5, lng: 19.0 }, { lat: 50.1, lng: 8.7 },
    { lat: 53.5, lng: -2.2 }, { lat: 57.7, lng: 11.9 }, { lat: 51.9, lng: 4.5 },
    { lat: 48.2, lng: 16.4 }, { lat: 55.9, lng: -3.2 }, { lat: 53.8, lng: -1.5 },
    { lat: 54.6, lng: -5.9 },
    { lat: 35.7, lng: 139.7 }, { lat: 34.7, lng: 135.5 },
  ],
  // FYE 2029 - 100 dots (51 NA, 42 EMEA, 7 APAC)
  [
    { lat: 28.5, lng: -81.4 }, { lat: 37.3, lng: -79.9 }, { lat: 38.3, lng: -85.8 },
    { lat: 43.1, lng: -77.6 }, { lat: 41.7, lng: -93.6 }, { lat: 36.1, lng: -95.9 },
    { lat: 32.8, lng: -79.9 }, { lat: 40.4, lng: -111.9 }, { lat: 44.6, lng: -63.6 },
    { lat: 47.9, lng: -97.0 }, { lat: 43.5, lng: -96.7 }, { lat: 46.8, lng: -100.8 },
    { lat: 38.0, lng: -97.3 }, { lat: 35.5, lng: -97.5 }, { lat: 32.3, lng: -90.2 },
    { lat: 30.5, lng: -91.1 }, { lat: 34.7, lng: -92.3 }, { lat: 39.0, lng: -80.5 },
    { lat: 44.3, lng: -69.8 }, { lat: 43.2, lng: -71.5 }, { lat: 41.6, lng: -72.7 },
    { lat: 40.2, lng: -74.8 }, { lat: 37.5, lng: -122.4 }, { lat: 33.4, lng: -117.6 },
    { lat: 47.2, lng: -120.7 }, { lat: 45.5, lng: -122.7 }, { lat: 43.8, lng: -120.5 },
    { lat: 36.8, lng: -119.8 }, { lat: 39.5, lng: -119.8 }, { lat: 41.2, lng: -104.8 },
    { lat: 46.6, lng: -112.0 }, { lat: 43.6, lng: -116.2 }, { lat: 33.5, lng: -86.8 },
    { lat: 35.0, lng: -85.3 }, { lat: 36.2, lng: -83.0 }, { lat: 38.2, lng: -84.3 },
    { lat: 42.3, lng: -83.0 }, { lat: 41.5, lng: -83.6 }, { lat: 39.3, lng: -82.1 },
    { lat: 40.8, lng: -81.4 }, { lat: 42.1, lng: -80.1 }, { lat: 44.5, lng: -88.0 },
    { lat: 43.0, lng: -89.4 }, { lat: 44.9, lng: -89.6 }, { lat: 41.9, lng: -87.6 },
    { lat: 40.6, lng: -89.6 }, { lat: 38.6, lng: -87.6 }, { lat: 39.8, lng: -89.6 },
    { lat: 42.7, lng: -84.6 }, { lat: 48.0, lng: -84.7 }, { lat: 52.1, lng: -106.7 },
    { lat: 46.1, lng: 14.5 }, { lat: 50.4, lng: 30.5 }, { lat: 52.5, lng: 13.4 },
    { lat: 51.5, lng: -3.2 }, { lat: 43.7, lng: 7.3 }, { lat: 45.8, lng: 15.9 },
    { lat: 38.7, lng: -9.1 }, { lat: 37.9, lng: 23.7 }, { lat: 44.4, lng: 26.1 },
    { lat: 42.7, lng: 23.3 }, { lat: 56.9, lng: 24.1 }, { lat: 59.4, lng: 24.7 },
    { lat: 54.7, lng: 25.3 }, { lat: 61.5, lng: 23.8 }, { lat: 64.1, lng: -21.9 },
    { lat: 60.5, lng: 5.3 }, { lat: 63.4, lng: 10.4 }, { lat: 58.4, lng: 15.6 },
    { lat: 56.2, lng: 15.9 }, { lat: 55.4, lng: 10.4 }, { lat: 57.0, lng: 9.9 },
    { lat: 51.4, lng: 7.0 }, { lat: 49.5, lng: 6.1 }, { lat: 47.1, lng: 15.4 },
    { lat: 46.8, lng: 8.2 }, { lat: 44.8, lng: 20.5 }, { lat: 45.3, lng: 14.4 },
    { lat: 42.4, lng: 19.3 }, { lat: 41.3, lng: 19.8 }, { lat: 39.9, lng: 32.9 },
    { lat: 41.0, lng: 29.0 }, { lat: 38.4, lng: 27.1 }, { lat: 37.6, lng: 26.9 },
    { lat: 35.3, lng: 25.1 }, { lat: 40.6, lng: 22.9 }, { lat: 44.8, lng: 11.3 },
    { lat: 43.8, lng: 11.2 }, { lat: 41.4, lng: 2.2 }, { lat: 39.5, lng: -0.4 },
    { lat: 36.7, lng: -4.4 }, { lat: 48.6, lng: 7.8 }, { lat: 45.2, lng: 5.7 },
    { lat: 43.6, lng: 1.4 },
    { lat: 35.0, lng: 136.9 }, { lat: 33.6, lng: 130.4 }, { lat: 43.1, lng: 141.3 },
    { lat: 36.3, lng: 139.9 }, { lat: 34.4, lng: 132.5 }, { lat: 38.3, lng: 140.9 },
    { lat: 35.2, lng: 137.0 },
  ],
];
const DEPLOYMENT_YEAR_VIEWS = [
  { label: 'FYE 2026', counts: { NA: 4, EMEA: 3, APAC: 0 } },
  { label: 'FYE 2027', counts: { NA: 11, EMEA: 4, APAC: 0 } },
  { label: 'FYE 2028', counts: { NA: 31, EMEA: 22, APAC: 2 } },
  { label: 'FYE 2029', counts: { NA: 51, EMEA: 42, APAC: 7 } },
].map((view, i, arr) => ({
  ...view,
  color: DEPLOYMENT_YEAR_COLORS[i],
  total: arr.slice(0, i + 1).reduce((sum, v) => sum + DEPLOYMENT_REGIONS.reduce((s, r) => s + v.counts[r.key], 0), 0),
}));

function normalizeR1000Copy(value) {
  if (typeof value !== 'string') {
    return value;
  }

  return value.replace(/\br-?1000\b/gi, 'r1000');
}

function matchesR1000SourceText(value, candidates) {
  return candidates.includes(normalizeR1000Copy(value));
}

export function getDefaultHeroCapabilityIndex(capabilityReel = []) {
  const scanIndex = capabilityReel.findIndex((item) => /scan/i.test(`${item?.eyebrow || ''} ${item?.title || ''}`.trim()));

  return scanIndex >= 0 ? scanIndex : 0;
}

function buildCompanyLogoSrc(fileName) {
  return `/customer-logos/${fileName.split('/').map(encodeURIComponent).join('/')}`;
}

function HeroCompanyLogoMarquee({ logos = HERO_COMPANY_LOGOS }) {
  const resolvedLogos = logos;

  if (!resolvedLogos.length) {
    return null;
  }

  return (
    <div className='hero-stage-partners' aria-label='Trusted organizations'>
      {[0, 1].map((setIndex) => (
        <div
          key={setIndex}
          className='hero-stage-partners-marquee'
          aria-hidden={setIndex === 1 ? 'true' : undefined}
        >
          {resolvedLogos.map((item) => (
            <span key={`${setIndex}-${item.id || item.file || item.text || item.name}`} className='hero-stage-partner'>
              {item.text ? (
                <span className='hero-stage-partner-text'>{item.text}</span>
              ) : (
                <OptimizedImage
                  src={item.direct ? item.file : buildCompanyLogoSrc(item.file)}
                  alt={setIndex === 0 ? item.name : ''}
                  className='hero-stage-partner-mark'
                  fit='contain'
                  sizes='360px'
                />
              )}
            </span>
          ))}
        </div>
      ))}
    </div>
  );
}

function buildHomeR1000RenderSection(sourceSection = {}) {
  return {
    ...sourceSection,
    type: 'renderShowcase',
    theme: sourceSection.theme || 'plain',
    anchorId: sourceSection.anchorId || 'meet-r-1000',
    title: HOME_R1000_RENDER_TITLE,
    body: sourceSection.body || HOME_R1000_RENDER_BODY,
    media: {
      ...(sourceSection.media || {}),
      type: 'image',
      src: HOME_R1000_RENDER_MEDIA_SRC,
      alt: '3D render of the r1000 microfactory.',
    },
    actions: sourceSection.actions?.length
      ? sourceSection.actions
      : [
          { label: 'Explore r1000', href: '/products/r1000' },
          { label: 'View Products', href: '/products' },
        ],
  };
}

function isR1000ProductVideoSection(section) {
  return (
    section?.type === 'mediaSpotlight' &&
    (section.title === R1000_PRODUCT_VIDEO_TITLE ||
      section.eyebrow === R1000_PRODUCT_VIDEO_SECTION.eyebrow ||
      section.media?.src === R1000_PRODUCT_VIDEO_SRC)
  );
}

function buildR1000ProductVideoSection(sourceSection = {}) {
  const sourceMediaIsVideo = sourceSection.media?.type === 'video';

  return {
    ...R1000_PRODUCT_VIDEO_SECTION,
    ...sourceSection,
    type: 'mediaSpotlight',
    eyebrow: sourceSection.eyebrow || R1000_PRODUCT_VIDEO_SECTION.eyebrow,
    title: sourceSection.title || R1000_PRODUCT_VIDEO_SECTION.title,
    body: sourceSection.body || R1000_PRODUCT_VIDEO_SECTION.body,
    media: {
      ...R1000_PRODUCT_VIDEO_SECTION.media,
      ...(sourceMediaIsVideo ? sourceSection.media : {}),
      type: 'video',
      src: sourceMediaIsVideo && sourceSection.media?.src ? sourceSection.media.src : R1000_PRODUCT_VIDEO_SRC,
      poster: sourceSection.media?.poster || R1000_PRODUCT_VIDEO_SECTION.media.poster,
      alt: sourceSection.media?.alt || R1000_PRODUCT_VIDEO_SECTION.media.alt,
    },
    actions: sourceSection.actions?.length ? sourceSection.actions : R1000_PRODUCT_VIDEO_SECTION.actions,
    pills: sourceSection.pills?.length ? sourceSection.pills : R1000_PRODUCT_VIDEO_SECTION.pills,
    invert: sourceSection.invert ?? R1000_PRODUCT_VIDEO_SECTION.invert,
  };
}

function isR1000TechnologyVideoSection(section) {
  return (
    section?.type === 'mediaSpotlight' &&
    (section.title === R1000_TECHNOLOGY_VIDEO_SECTION.title ||
      section.media?.src === R1000_TECHNOLOGY_VIDEO_SRC)
  );
}

function buildHomeTechnologyFlowSection(sourceSection = {}) {
  return {
    ...HOME_TECHNOLOGY_FLOW_SECTION,
    ...sourceSection,
    type: 'process',
    theme: sourceSection.theme || HOME_TECHNOLOGY_FLOW_SECTION.theme,
    eyebrow: sourceSection.eyebrow || HOME_TECHNOLOGY_FLOW_SECTION.eyebrow,
    title: sourceSection.title || HOME_TECHNOLOGY_FLOW_SECTION.title,
    items: sourceSection.items?.length ? sourceSection.items : HOME_TECHNOLOGY_FLOW_SECTION.items,
  };
}

function buildSolutionsProductChooserSection(sourceSection = {}) {
  return {
    ...sourceSection,
    ...SOLUTIONS_PRODUCT_CHOOSER_SECTION,
    theme: sourceSection.theme || SOLUTIONS_PRODUCT_CHOOSER_SECTION.theme || 'plain',
  };
}

function renderTextWithLineBreaks(value) {
  if (typeof value !== 'string' || !value.includes('\n')) {
    return value;
  }

  return value.split('\n').map((line, index) => (
    <React.Fragment key={`${line}-${index}`}>
      {index > 0 ? <br /> : null}
      {line}
    </React.Fragment>
  ));
}

function SectionHeader({ eyebrow, title, body, className = '' }) {
  if (!eyebrow && !title && !body) {
    return null;
  }

  return (
    <div className={`section-header reveal-item ${className}`.trim()}>
      {eyebrow ? <p className='section-label'>{eyebrow}</p> : null}
      {title ? <h2>{renderTextWithLineBreaks(title)}</h2> : null}
      {body ? <p className='section-copy'>{body}</p> : null}
    </div>
  );
}

function formatArticleDate(value) {
  if (!value) {
    return '';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return '';
  }

  return parsedDate.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function formatArticleMeta(item) {
  return [item.category || item.sector, formatArticleDate(item.publishedAt)].filter(Boolean).join(' · ');
}

export function normalizePageHero(page) {
  if (page?.path === '/products' || page?.path === '/solutions') {
    return {
      ...(page.hero || {}),
      ...SOLUTIONS_EXPLORE_HERO,
    };
  }

  return page?.hero || {};
}

export function normalizePageSections(page) {
  const sections = page?.sections || [];

  if (page?.path === '/technology') {
    const filteredSections = sections.filter((section) => !(section.type === 'process' && section.title === TECHNOLOGY_FLOW_TITLE));
    const platformFeaturesIndex = filteredSections.findIndex(
      (section) => section.type === 'cards' && section.title === TECHNOLOGY_PLATFORM_FEATURES_TITLE,
    );
    const whyItMattersIndex = filteredSections.findIndex(
      (section) => section.type === 'mediaSpotlight' && section.title === TECHNOLOGY_WHY_IT_MATTERS_TITLE,
    );

    if (platformFeaturesIndex >= 0 && whyItMattersIndex > platformFeaturesIndex) {
      const reorderedSections = [...filteredSections];
      const [whyItMattersSection] = reorderedSections.splice(whyItMattersIndex, 1);
      reorderedSections.splice(platformFeaturesIndex, 0, whyItMattersSection);
      return reorderedSections;
    }

    return filteredSections;
  }

  if (page?.path === '/products' || page?.path === '/solutions') {
    const sourceProductChooserSection = sections.find((section) => section.type === 'productChooser');
    const ctaBanner = sections.find((section) => section.type === 'ctaBanner') || {
      type: 'ctaBanner',
      eyebrow: 'Next step',
      title: 'Ready to see it in action?',
      body: 'Talk to the team about your post-processing challenge.',
      primaryAction: { label: 'Book a demo', href: '/request-demo' },
    };

    return [buildSolutionsProductChooserSection(sourceProductChooserSection), SOLUTION_AREAS_SECTION, ctaBanner];
  }

  if (page?.path === '/solutions/r1000') {
    // Remove render showcase (duplicate of hero) and the "Why it matters" fluff section
    // Merge both videos into the product video section with tabs
    const productVideoSection = sections.find(isR1000ProductVideoSection);

    // Build a single tabbed video section
    const tabbedVideoSection = {
      ...buildR1000ProductVideoSection(productVideoSection),
      type: 'tabbedVideo',
      eyebrow: 'r1000 in action',
      title: 'Watch the r1000 work.',
      body: 'Adaptive scanning, toolpath control, and finishing intelligence in one compact production microfactory.',
      tabs: [
        {
          label: 'Product overview',
          media: {
            type: 'video',
            src: R1000_PRODUCT_VIDEO_SRC,
            poster: HOME_R1000_RENDER_MEDIA_SRC,
            alt: 'r1000 finishing and deployment sequence.',
          },
        },
        {
          label: 'In production',
          media: {
            type: 'video',
            src: R1000_TECHNOLOGY_VIDEO_SRC,
            poster: HOME_R1000_RENDER_MEDIA_SRC,
            alt: 'r1000 slideshow sequence.',
          },
        },
      ],
    };

    const cleanSections = sections.filter(
      (section) =>
        section.type !== 'renderShowcase' &&
        section.anchorId !== 'meet-r1000' &&
        section.anchorId !== 'meet-r-1000' &&
        !isR1000ProductVideoSection(section) &&
        !isR1000TechnologyVideoSection(section),
    ).map((section) => {
      // Fix the "See the company story" CTA on any remaining sections
      if (section.actions) {
        return {
          ...section,
          actions: section.actions.map((action) =>
            action.href === '/company' ? { label: 'Book a demo', href: '/request-demo' } : action,
          ),
        };
      }
      return section;
    });

    const result = [tabbedVideoSection, ...cleanSections];

    return result;
  }

  if (page?.path === '/company') {
    const companySectionsWithoutModel = sections.filter(
      (section) => !(section.type === 'cards' && section.title === COMPANY_MODEL_SECTION_TITLE),
    );
    const normalizedSections = companySectionsWithoutModel.filter(
      (section) => !(section.type === 'timeline' && section.title === COMPANY_TIMELINE_SECTION_TITLE) && section.type !== 'marketInsights' && section.type !== 'scrollPrompt',
    );

    // Inject deployment map after market traction
    if (!normalizedSections.some((s) => s.type === 'deploymentMap')) {
      const tractionIndex = normalizedSections.findIndex((section) => section.type === 'marketTraction');
      const insertAt = tractionIndex >= 0 ? tractionIndex + 1 : normalizedSections.length;
      normalizedSections.splice(insertAt, 0, { type: 'deploymentMap' });
    }

    const teamSectionIndex = normalizedSections.findIndex((section) => section.type === 'collectionPreview' && section.collection === 'teamMembers');

    // Team has its own /leadership page — remove from company page
    if (teamSectionIndex >= 0) {
      normalizedSections.splice(teamSectionIndex, 1);
    }

    return normalizedSections;
  }

  if (page?.path !== '/') {
    return sections;
  }

  const latestNormalizedSections = sections.map((section) => {
    if (section.type !== 'statBand' || section.title !== HOME_LATEST_SOURCE_TITLE) {
      return section;
    }

    return {
      type: 'collectionPreview',
      title: 'Latest',
      collection: 'resourceArticles',
      theme: section.theme || 'plain',
      anchorId: LATEST_SECTION_ID,
    };
  });

  const sourceR1000Section = latestNormalizedSections.find(
    (section) =>
      section.type === 'mediaSpotlight' &&
      (matchesR1000SourceText(section.eyebrow, HOME_R1000_SOURCE_EYEBROWS) ||
        matchesR1000SourceText(section.title, HOME_R1000_SOURCE_TITLES)),
  );
  const sourceRenderSection = latestNormalizedSections.find(
    (section) =>
      section.type === 'renderShowcase' ||
      normalizeR1000Copy(section.title) === HOME_R1000_RENDER_TITLE ||
      section.anchorId === 'meet-r-1000',
  );
  const sourceTechnologyFlowSection = latestNormalizedSections.find(
    (section) => section.type === 'process' && section.title === TECHNOLOGY_FLOW_TITLE,
  );
  const homeR1000RenderSection = buildHomeR1000RenderSection(sourceRenderSection);
  const homeTechnologyFlowSection = buildHomeTechnologyFlowSection(sourceTechnologyFlowSection);
  const reorderedSections = latestNormalizedSections.filter(
    (section) =>
      section !== sourceR1000Section &&
      section !== sourceRenderSection &&
      section !== sourceTechnologyFlowSection &&
      !(section.type === 'cards' && (HOME_SIGNALS_TITLES.includes(section.title) || section.title === HOME_NEXT_ROUTES_TITLE)) &&
      !(section.type === 'collectionPreview' && section.title === HOME_CASE_STUDIES_PREVIEW_TITLE),
  );

  reorderedSections.unshift(HOME_PROBLEM_SECTION, homeTechnologyFlowSection, homeR1000RenderSection);

  const latestSectionIndex = reorderedSections.findIndex(
    (section) => section.type === 'collectionPreview' && section.anchorId === LATEST_SECTION_ID,
  );
  if (latestSectionIndex !== -1) {
    reorderedSections.splice(latestSectionIndex + 1, 0, { type: 'testimonials' });
  } else {
    reorderedSections.push({ type: 'testimonials' });
  }

  return reorderedSections;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }

    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updatePreference = () => setPrefersReducedMotion(mediaQuery.matches);

    updatePreference();

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', updatePreference);
      return () => mediaQuery.removeEventListener('change', updatePreference);
    }

    mediaQuery.addListener(updatePreference);
    return () => mediaQuery.removeListener(updatePreference);
  }, []);

  return prefersReducedMotion;
}

function ScrollReveal({
  as: Component = 'div',
  className = '',
  children,
  threshold = 0.01,
  rootMargin = '0px 0px 8% 0px',
  ...props
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [isVisible, setIsVisible] = useState(prefersReducedMotion);
  const elementRef = useRef(null);

  useEffect(() => {
    if (prefersReducedMotion || typeof window === 'undefined' || typeof IntersectionObserver !== 'function') {
      setIsVisible(true);
      return undefined;
    }

    const node = elementRef.current;

    if (!node) {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      {
        threshold,
        rootMargin,
      },
    );

    observer.observe(node);

    return () => observer.disconnect();
  }, [prefersReducedMotion, rootMargin, threshold]);

  return (
    <Component ref={elementRef} className={`scroll-reveal-group ${isVisible ? 'is-visible' : ''} ${className}`.trim()} {...props}>
      {children}
    </Component>
  );
}

function revealDelayStyle(index, step = 45, baseDelay = 0) {
  return {
    '--reveal-delay': `${baseDelay + index * step}ms`,
  };
}

function HeroStatValue({ stat }) {
  if (typeof stat.number !== 'number') {
    return (
      <span className='hero-metric-value-text'>
        {stat.prefix || ''}
        {stat.value || ''}
        {stat.suffix || ''}
      </span>
    );
  }

  return (
    <span className='hero-metric-value-shell'>
      {stat.prefix ? <span className='hero-metric-affix'>{stat.prefix}</span> : null}
      <CountingNumber
        className='hero-metric-number'
        number={stat.number}
        fromNumber={stat.fromNumber ?? 0}
        decimalPlaces={stat.decimalPlaces}
        inView={true}
        inViewMargin='-10% 0px'
      />
      {stat.suffix ? <span className='hero-metric-affix'>{stat.suffix}</span> : null}
    </span>
  );
}

function HeroMetrics({ stats }) {
  if (!stats?.length) {
    return null;
  }

  return (
    <div className='hero-metrics reveal-item' aria-label='Key performance metrics'>
      {stats.map((stat, index) => (
        <article
          key={`${stat.label}-${stat.value || stat.number || ''}`}
          className='hero-metric reveal-item'
          style={revealDelayStyle(index, 80, 120)}
        >
          <HeroStatValue stat={stat} />
          <span className='hero-metric-label'>{stat.label}</span>
          {stat.detail ? <span className='hero-metric-detail'>{stat.detail}</span> : null}
        </article>
      ))}
    </div>
  );
}

function Hero({ hero, isHomePage }) {
  const heroId = useId();
  const prefersReducedMotion = usePrefersReducedMotion();
  const capabilityReel = React.useMemo(() => hero.capabilityReel || [], [hero.capabilityReel]);
  const isMinimalHero = hero.variant === 'cinematicMinimal';
  const isSimpleIntro = !isHomePage && !isMinimalHero;
  const defaultCapabilityIndex = getDefaultHeroCapabilityIndex(capabilityReel);
  const [activeCapability, setActiveCapability] = useState(defaultCapabilityIndex);
  const [visibleCapability, setVisibleCapability] = useState(defaultCapabilityIndex);

  useEffect(() => {
    const timer = window.setTimeout(() => setVisibleCapability(activeCapability), 300);
    return () => window.clearTimeout(timer);
  }, [activeCapability]);
  const activeCapabilityItem = capabilityReel[activeCapability];
  const activeMedia = activeCapabilityItem?.media || hero.media;
  const hasVideo = activeMedia?.type === 'video' && !prefersReducedMotion;
  const fallbackMedia = activeMedia?.poster || activeMedia?.src || hero.media?.poster || hero.media?.src;
  const heroTag = hero.eyebrow || activeCapabilityItem?.eyebrow || activeCapabilityItem?.title;
  const heroLogo = hero.logo || HERO_CENTER_LOGO;
  const showMinimalHeroNav = capabilityReel.length > 0;
  const showHeroCompanyLogos = hero.partnerLogosInHero && HERO_COMPANY_LOGOS.length > 0;
  const activeAnnouncement = activeCapabilityItem
    ? `${activeCapabilityItem.title}. ${activeCapabilityItem.statement || ''}`.trim()
    : '';

  useEffect(() => {
    if (prefersReducedMotion) return;
    const urls = capabilityReel
      .map((item) => item.media?.type === 'video' && item.media.src)
      .filter(Boolean);
    urls.slice(1).forEach((url) => {
      const v = document.createElement('video');
      v.preload = 'auto';
      v.src = url;
      v.load();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && isMinimalHero) {
      console.debug('[Hero] active capability changed', {
        index: activeCapability,
        label: activeCapabilityItem?.eyebrow || activeCapabilityItem?.title,
        mediaType: activeMedia?.type,
        mediaSrc: activeMedia?.src,
      });
    }
  }, [activeCapability, activeCapabilityItem, activeMedia, isMinimalHero]);

  useEffect(() => {
    setActiveCapability(defaultCapabilityIndex);
  }, [defaultCapabilityIndex, hero.title]);

  useEffect(() => {
    if (capabilityReel.length < 2 || prefersReducedMotion) {
      return undefined;
    }

    const duration = capabilityReel[activeCapability]?.duration || 8000;
    const timer = window.setTimeout(() => {
      setActiveCapability((current) => (current + 1) % capabilityReel.length);
    }, duration);

    return () => window.clearTimeout(timer);
  }, [activeCapability, capabilityReel, prefersReducedMotion]);

  const renderHeroMediaStage = ({ minimal = false } = {}) => (
    <div className='hero-media-stage'>
      {hasVideo ? (
        <LazyVideo
          key={activeMedia?.src}
          className='hero-video-shell'
          videoClassName='hero-video'
          src={activeMedia?.src}
          showLoadingOverlay={false}
          eager={activeCapability === defaultCapabilityIndex}
        />
      ) : (
        <div className='hero-image-stage'>
          <OptimizedImage className='hero-image' src={fallbackMedia} alt={activeMedia?.alt || hero.title} />
        </div>
      )}
      {minimal && showMinimalHeroNav ? (
        <div className='hero-stage-top'>
          <div className='hero-capability-nav' aria-label='Robot capabilities'>
            {capabilityReel.map((item, index) => (
              <button
                key={item.title}
                type='button'
                className={`hero-capability-label-btn ${index === activeCapability ? 'active' : ''}`.trim()}
                onClick={() => setActiveCapability(index)}
                onFocus={() => setActiveCapability(index)}
                aria-pressed={index === activeCapability}
              >
                {item.eyebrow || item.title}
              </button>
            ))}
          </div>
        </div>
      ) : null}
      {minimal && heroLogo ? (
        <div className='hero-stage-brand'>
          <div className='hero-stage-logo-shell'>
            <OptimizedImage
              className='hero-stage-logo'
              src={heroLogo}
              alt={hero.logoAlt || 'Rivelin Robotics'}
              fit='contain'
              sizes='(min-width: 1100px) 320px, 42vw'
              loading='eager'
              decoding='sync'
            />
          </div>
        </div>
      ) : null}
    </div>
  );

  if (isSimpleIntro) {
    return (
      <ScrollReveal as='section' className='page-intro'>
        <div className='page-intro-copy reveal-item'>
          {hero.eyebrow ? <p className='section-label'>{hero.eyebrow}</p> : null}
          {hero.title === 'Rivelin Robotics'
            ? <div style={{ paddingBottom: '0.4rem' }}><img src='https://images.ctfassets.net/bjia12x471fb/5I3OIMUqV1G3lXISdaq33c/33ec06355d216ac1307f97c30e904332/rivelin_logos_rivelin_logo-idevice_gr-drk_4x.png' alt='Rivelin Robotics' className='page-intro-wordmark' /></div>
            : <h1>{hero.title}</h1>
          }
          {hero.body ? <p className='page-intro-body'>{hero.body}</p> : null}
          {hero.primaryAction || hero.secondaryAction ? (
            <div className='hero-actions page-intro-actions'>
              {hero.primaryAction ? (
                <Link className='primary-cta' to={normalizeSiteHref(hero.primaryAction.href)}>
                  {hero.primaryAction.label}
                </Link>
              ) : null}
              {hero.secondaryAction ? (
                <Link className='secondary-cta' to={normalizeSiteHref(hero.secondaryAction.href)}>
                  {hero.secondaryAction.label}
                </Link>
              ) : null}
            </div>
          ) : null}
        </div>
        <HeroMetrics stats={hero.stats} />
      </ScrollReveal>
    );
  }

  if (isMinimalHero) {
    return (
      <ScrollReveal as='section' className='page-hero page-hero-minimal'>
        <div className='page-hero-media reveal-item'>{renderHeroMediaStage({ minimal: true })}</div>
        <div className='visually-hidden' aria-live='polite'>
          {activeAnnouncement}
        </div>
        {showHeroCompanyLogos ? (
          <div className='hero-logos-below'>
            <HeroCompanyLogoMarquee />
            {hero.partnerLabel ? <p className='hero-logos-label'>{hero.partnerLabel}</p> : null}
          </div>
        ) : null}
      </ScrollReveal>
    );
  }

  return (
    <ScrollReveal as='section' className='page-hero'>
      <div className='page-hero-media reveal-item'>{renderHeroMediaStage()}</div>
      <div className='page-hero-grid'>
        <div className='page-hero-content reveal-item' style={revealDelayStyle(0, 0, 110)}>
          <div className='page-hero-copy'>
            {heroTag ? <p className='hero-active-tag'>{heroTag}</p> : null}
            <h1>{hero.title}</h1>
            <p className='hero-body'>{hero.body}</p>
            <div className='hero-actions'>
              {hero.primaryAction ? (
                <Link className='primary-cta' to={normalizeSiteHref(hero.primaryAction.href)}>
                  {hero.primaryAction.label}
                </Link>
              ) : null}
              {hero.secondaryAction ? (
                <Link className='secondary-cta' to={normalizeSiteHref(hero.secondaryAction.href)}>
                  {hero.secondaryAction.label}
                </Link>
              ) : null}
            </div>
            {hero.stats?.length ? (
              <div className='hero-stats'>
                {hero.stats.map((stat) => (
                  <div key={stat.label} className='hero-stat'>
                    <span className='hero-stat-value'>{stat.value}</span>
                    <span className='hero-stat-label'>{stat.label}</span>
                  </div>
                ))}
              </div>
            ) : null}
          </div>
        </div>
        {capabilityReel.length ? (
          <div className='hero-capability-details reveal-item' style={revealDelayStyle(1, 0, 180)}>
            <div className='hero-capability-panels'>
              {capabilityReel.map((item, index) => (
                <div
                  key={`${item.title}-${index}`}
                  id={`${heroId}-panel-${index}`}
                  role='tabpanel'
                  aria-labelledby={`${heroId}-tab-${index}`}
                  className={`hero-capability-panel ${index === visibleCapability ? 'is-active' : 'is-hidden'}`.trim()}
                  aria-hidden={index !== visibleCapability}
                >
                  <span className='hero-media-index'>{String(index + 1).padStart(2, '0')}</span>
                  <div className='hero-media-copy'>
                    <p className='section-label'>{item.eyebrow || 'Robot capability'}</p>
                    <h2>{item.title}</h2>
                    {item.statement ? <p>{item.statement}</p> : null}
                  </div>
                </div>
              ))}
            </div>
            <div className='hero-capability-nav' role='tablist' aria-label='Robot capabilities'>
              {capabilityReel.map((item, index) => (
                <button
                  key={item.title}
                  id={`${heroId}-tab-${index}`}
                  type='button'
                  role='tab'
                  className={`hero-capability-button ${index === activeCapability ? 'active' : ''}`.trim()}
                  onClick={() => setActiveCapability(index)}
                  onFocus={() => setActiveCapability(index)}
                  aria-selected={index === activeCapability}
                  aria-controls={`${heroId}-panel-${index}`}
                  tabIndex={index === activeCapability ? 0 : -1}
                >
                  <span className='hero-capability-count'>{String(index + 1).padStart(2, '0')}</span>
                  <span className='hero-capability-label'>{item.title}</span>
                </button>
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </ScrollReveal>
  );
}

function LogoStripSection({ section }) {
  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section logo-strip-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
      <div className='logo-strip'>
        {section.items.map((item, index) => (
          <div key={item.name} className='logo-tile reveal-item' style={revealDelayStyle(index)}>
            <img src={item.image} alt={item.name} />
            <span className='logo-name'>{item.name}</span>
          </div>
        ))}
      </div>
    </ScrollReveal>
  );
}

function CardsSection({ section }) {
  return (
    <ScrollReveal as='section' id={section.anchorId || undefined} className={`content-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}>
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
      <div className={`card-grid ${section.columns ? `card-grid-${section.columns}col` : ''}`.trim()}>
        {section.items.map((item, index) => (
          <article key={item.title} className='content-card reveal-item' style={revealDelayStyle(index)}>
            {section.showNumbers ? <span className='content-card-number'>{String(index + 1).padStart(2, '0')}</span> : null}
            {item.eyebrow ? <p className='card-eyebrow'>{item.eyebrow}</p> : null}
            <h3>{item.title}</h3>
            <p>{item.body}</p>
            {item.href ? (
              <Link className='inline-link inline-button' to={normalizeSiteHref(item.href)}>
                {item.actionLabel || 'Explore'}
              </Link>
            ) : null}
          </article>
        ))}
      </div>
    </ScrollReveal>
  );
}

function ProcessSection({ section }) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [equationCycle, setEquationCycle] = useState(0);

  useEffect(() => {
    if (!section.equation || prefersReducedMotion) {
      return undefined;
    }

    const interval = window.setInterval(() => {
      setEquationCycle((value) => value + 1);
    }, 8400);

    return () => window.clearInterval(interval);
  }, [prefersReducedMotion, section.equation]);

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      threshold={0.15}
      className={`content-section process-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
      {section.equation ? (
        <div className='process-equation reveal-item' key={equationCycle}>
          <div className='equation-inputs'>
            {section.equation.inputs.map((input, i) => (
              <React.Fragment key={input}>
                {i > 0 && <span className='equation-operator'>+</span>}
                <span className='equation-term'>{input}</span>
              </React.Fragment>
            ))}
          </div>
          <span className='equation-operator equation-equals'>=</span>
          <div className='equation-outputs'>
            {section.equation.outputs.map((output) => (
              <span key={output} className='equation-result'>{output}</span>
            ))}
          </div>
        </div>
      ) : section.stats?.length ? (
        <>
          <HeroMetrics stats={section.stats} />
          <div className='process-metrics-divider' />
        </>
      ) : null}
      {section.stepsEyebrow ? <p className='section-label process-steps-label'>{section.stepsEyebrow}</p> : null}
      <div className='process-grid'>
        {section.items.map((item, index) => (
          <article key={item.title} className='process-card reveal-item' style={revealDelayStyle(index, 200)}>
            <div className='process-card-header'>
              <span className='process-count'>{index + 1}</span>
              <h3>{item.title}</h3>
            </div>
            <p>{item.body}</p>
          </article>
        ))}
      </div>
    </ScrollReveal>
  );
}

function MediaSpotlightSection({ section }) {
  const isTeamSection = section.eyebrow === 'The team';
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 860;
  const { register } = useScrollTracker();
  const elRef = useRef(null);
  const sectionRef = useCallback((el) => {
    elRef.current = el;
    if (isTeamSection) register('team', el);
  }, [isTeamSection, register]);

  useEffect(() => {
    if (!isTeamSection || isMobileView) return;
    return () => {};
  }, [isTeamSection, isMobileView]);

  const mediaIsVideo = section.media?.type === 'video';
  const mediaAspectRatioClass = section.mediaAspectRatio === 'pinterest' ? 'is-pinterest-ratio' : section.mediaAspectRatio === 'auto' ? 'is-auto-height' : section.mediaAspectRatio === 'square' ? 'is-square-ratio' : '';
  const hasCopy = section.eyebrow || section.title || section.body || section.actions?.length;

  const content = (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section media-spotlight ${section.centered ? 'is-centered' : ''} ${section.invert ? 'invert' : ''} ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      {hasCopy && (
        <div className='media-spotlight-copy reveal-item'>
          <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
          {section.actions?.length ? (
            <div className='hero-actions'>
              {section.actions.map((action, index) => (
                <Link key={action.href} className={index === 0 ? 'primary-cta' : 'secondary-cta'} to={normalizeSiteHref(action.href)}>
                  {action.label}
                </Link>
              ))}
            </div>
          ) : null}
        </div>
      )}
      {section.media?.type === 'videoPlaceholder' ? (
        <div className='media-spotlight-asset reveal-item' style={revealDelayStyle(0, 0, 140)}>
          <div className='dual-video-placeholder' />
        </div>
      ) : section.media?.src ? (
        <div
          className={`media-spotlight-asset ${mediaIsVideo ? 'has-video' : ''} ${mediaAspectRatioClass} reveal-item`.trim()}
          style={revealDelayStyle(0, 0, 140)}
        >
          {mediaIsVideo ? (
            <DualVideoPlayer src={section.media.src} />
          ) : section.media.skipOptimization ? (
            <img src={section.media.src} alt={section.media?.alt || section.title} className='spotlight-image' loading='lazy' decoding='async' />
          ) : (
            <OptimizedImage src={section.media.src} alt={section.media?.alt || section.title} className='spotlight-image' />
          )}
        </div>
      ) : null}
    </ScrollReveal>
  );

  if (isTeamSection) {
    return <div ref={sectionRef}>{content}</div>;
  }

  return content;
}

function RenderShowcaseSection({ section }) {
  const [isImageReady, setIsImageReady] = useState(false);

  const isVideoPlaceholder = section.media?.type === 'videoPlaceholder';
  const isVideo = section.media?.type === 'video' && section.media?.src;

  if (!isVideoPlaceholder && !isVideo && !section.media?.src) {
    return null;
  }

  const renderShellStyle = {
    ...revealDelayStyle(0, 0, 110),
    '--decorative-graphic': `url('${RIVELIN_BACKGROUND_GRAPHIC}')`,
  };

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section render-showcase-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <div className='render-showcase-copy'>
        <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} className='centered' />
        {section.actions?.length ? (
          <div className='hero-actions render-showcase-actions reveal-item' style={revealDelayStyle(0, 0, 180)}>
            {section.actions.map((action, index) => (
              <Link key={action.href} className={index === 0 ? 'primary-cta' : 'secondary-cta'} to={normalizeSiteHref(action.href)}>
                {action.label}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
      <div className='render-showcase-shell reveal-item' style={renderShellStyle}>
        {isVideoPlaceholder ? (
          <div className='dual-video-placeholder render-showcase-video' />
        ) : isVideo ? (
          <DualVideoPlayer src={section.media.src} className='render-showcase-video' />
        ) : (
          <>
            {!isImageReady ? (
              <div className='render-showcase-loading' aria-hidden='true'>
                <img src={HOME_R1000_RENDER_LOADING_LOGO} alt='' className='render-showcase-loading-logo' />
              </div>
            ) : null}
            <OptimizedImage
              src={section.media.src}
              alt={section.media.alt || section.title}
              className={`render-showcase-image ${isImageReady ? 'is-ready' : ''}`.trim()}
              fit='contain'
              sizes='(min-width: 1200px) 880px, (min-width: 768px) 72vw, 92vw'
              loading='eager'
              fetchPriority='high'
              onLoad={() => setIsImageReady(true)}
              onError={() => setIsImageReady(true)}
            />
          </>
        )}
      </div>
    </ScrollReveal>
  );
}

function ProductChooserSection({ section }) {
  if (!section.items?.length) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section product-chooser-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} className='centered' />
      <div className='product-chooser-grid'>
        {section.items.map((item, index) => (
          <article
            key={item.title}
            className='product-chooser-card reveal-item'
            style={revealDelayStyle(index, 90, 80)}
          >
            <div className={`product-chooser-visual${item.mediaFit === 'cover' ? ' is-cover' : ''}`}>
              {item.media?.src ? (
                item.href ? (
                  <Link to={normalizeSiteHref(item.href)} tabIndex={-1} aria-hidden='true'>
                    <OptimizedImage
                      src={item.media.src}
                      alt={item.media.alt || item.title}
                      className='product-chooser-image'
                      fit={item.mediaFit || 'contain'}
                      sizes='(min-width: 1100px) 42vw, 92vw'
                    />
                  </Link>
                ) : (
                  <OptimizedImage
                    src={item.media.src}
                    alt={item.media.alt || item.title}
                    className='product-chooser-image'
                    fit={item.mediaFit || 'contain'}
                    sizes='(min-width: 1100px) 42vw, 92vw'
                  />
                )
              ) : null}
            </div>
            <div className='product-chooser-copy'>
              <h3>{item.title}</h3>
              <p>{item.body}</p>
              {item.href ? (
                <Link className='primary-cta product-chooser-action' to={normalizeSiteHref(item.href)}>
                  {item.actionLabel || 'Explore'}
                </Link>
              ) : null}
            </div>
          </article>
        ))}
      </div>
    </ScrollReveal>
  );
}

function StatBandSection({ section, collections }) {
  const items = section.collection ? collections[section.collection] || [] : section.items || [];

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section stat-band-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <SectionHeader title={section.title} />
      <div className='stat-band'>
        {items.map((item, index) => (
          <article key={`${item.value}-${item.label}`} className='stat-band-card reveal-item' style={revealDelayStyle(index)}>
            <span className='stat-band-value'>{item.value}</span>
            <span className='stat-band-label'>{item.label}</span>
            <p>{item.detail}</p>
          </article>
        ))}
      </div>
    </ScrollReveal>
  );
}



function useScrollProgress(ref, count, offsetRatio = 0.4) {
  const [index, setIndex] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    function onScroll() {
      const rect = el.getBoundingClientRect();
      const start = window.innerHeight * offsetRatio;
      const scrollable = el.offsetHeight - start;
      if (scrollable <= 0) return;
      const progress = Math.max(0, Math.min(1, (start - rect.top) / scrollable));
      setIndex(Math.min(Math.floor(progress * count), count - 1));
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [ref, count, offsetRatio]);

  return [index, setIndex];
}

function DeploymentMapSection() {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 860;
  const wrapperRef = useRef(null);
  const [activeViewIndex, setActiveViewIndex] = useScrollProgress(wrapperRef, DEPLOYMENT_YEAR_VIEWS.length);
  const selectedView = DEPLOYMENT_YEAR_VIEWS[activeViewIndex] || DEPLOYMENT_YEAR_VIEWS[0];
  const stepRefs = useRef([]);
  const scrollContainerRef = useRef(null);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const observers = stepRefs.current.map((el, index) => {
      if (!el) return null;
      const observer = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setActiveViewIndex(index);
          }
        },
        { root: container, threshold: 0.6 },
      );
      observer.observe(el);
      return observer;
    });

    return () => observers.forEach((obs) => obs?.disconnect());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Build cumulative dot markers up to current year, coloured per year
  const markers = [];
  for (let i = 0; i <= activeViewIndex; i++) {
    DEPLOYMENT_YEAR_MARKERS[i].forEach((m) => {
      markers.push({ ...m, color: DEPLOYMENT_YEAR_COLORS[i] });
    });
  }

  if (isMobileView) {
    return (
      <ScrollReveal as='section' className='content-section'>
        <div className='deployment-map-block deployment-map-block-mobile reveal-item' style={{ background: DEPLOYMENT_YEAR_BG_COLORS[activeViewIndex] }}>
          <div className='deployment-map-tabs deployment-map-tabs-mobile' aria-label='Deployment fiscal year'>
            {DEPLOYMENT_YEAR_VIEWS.map((view, index) => (
              <button
                key={view.label}
                type='button'
                className={`deployment-map-tab ${index === activeViewIndex ? 'is-active' : ''}`.trim()}
                aria-pressed={index === activeViewIndex}
                onClick={() => setActiveViewIndex(index)}
                style={index === activeViewIndex
                  ? { borderColor: DEPLOYMENT_YEAR_COLORS[index], color: DEPLOYMENT_YEAR_COLORS[index], fontWeight: 700, background: DEPLOYMENT_YEAR_BG_COLORS[index], boxShadow: 'none' }
                  : { color: DEPLOYMENT_YEAR_COLORS[index] }}
              >
                {view.label}
              </button>
            ))}
          </div>
          <div className='deployment-map-summary'>
            <div className='deployment-map-total'>
              <strong>{selectedView.total}</strong>
              <span>Total deployments</span>
            </div>
            <div className='deployment-map-region-list'>
              {DEPLOYMENT_REGIONS.map((region) => (
                <span key={region.key} className='deployment-map-region'>
                  <span>{region.label}</span>
                  <strong>{selectedView.counts[region.key]}</strong>
                </span>
              ))}
            </div>
          </div>
          <div className='deployment-map-stage' style={{ borderColor: DEPLOYMENT_YEAR_COLORS[activeViewIndex], background: DEPLOYMENT_YEAR_BG_COLORS[activeViewIndex] }}>
            <DeploymentWorldMap className='deployment-map-svg' markers={markers} bgColor={DEPLOYMENT_YEAR_BG_COLORS[activeViewIndex]} zoom={0.25} interactive={true} />
            <span className='deployment-map-drag-hint' aria-hidden='true'>
              <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'>
                <polyline points='5 9 2 12 5 15' /><polyline points='9 5 12 2 15 5' /><polyline points='15 19 12 22 9 19' /><polyline points='19 9 22 12 19 15' /><line x1='2' y1='12' x2='22' y2='12' /><line x1='12' y1='2' x2='12' y2='22' />
              </svg>
              Drag to explore
            </span>
          </div>
          <div className='deployment-year-legend'>
            {DEPLOYMENT_YEAR_VIEWS.slice(0, activeViewIndex + 1).map((view) => (
              <span key={view.label} className='deployment-year-legend-item'>
                <span className='deployment-year-legend-swatch' style={{ background: view.color }} />
                <span>{view.label}</span>
              </span>
            ))}
          </div>
        </div>
      </ScrollReveal>
    );
  }

  return (
    <div className='deployment-map-scroll-wrapper' ref={wrapperRef}>
      <div className='deployment-map-sticky'>
        <div className='deployment-map-block reveal-item' style={{ background: DEPLOYMENT_YEAR_BG_COLORS[activeViewIndex] }}>
          <div className='deployment-map-heading'>
            <div>
              <h3 className='deployment-map-title'>Deployment footprint</h3>
            </div>
            <div className='deployment-map-tabs' aria-label='Deployment fiscal year'>
              {DEPLOYMENT_YEAR_VIEWS.map((view, index) => (
                <button
                  key={view.label}
                  type='button'
                  className={`deployment-map-tab ${index === activeViewIndex ? 'is-active' : ''}`.trim()}
                  aria-pressed={index === activeViewIndex}
                  onClick={() => setActiveViewIndex(index)}
                  style={index === activeViewIndex ? { borderColor: DEPLOYMENT_YEAR_COLORS[index], background: DEPLOYMENT_YEAR_BG_COLORS[index], color: DEPLOYMENT_YEAR_COLORS[index], fontWeight: 700 } : { borderColor: DEPLOYMENT_YEAR_BG_COLORS[index], color: DEPLOYMENT_YEAR_COLORS[index] }}
                >
                  {view.label}
                </button>
              ))}
            </div>
          </div>
          <div className='deployment-map-summary'>
            <div className='deployment-map-total'>
              <strong>{selectedView.total}</strong>
              <span>Total deployments</span>
            </div>
            <div className='deployment-map-region-list'>
              {DEPLOYMENT_REGIONS.map((region) => (
                <span key={region.key} className='deployment-map-region'>
                  <span>{region.label}</span>
                  <strong>{selectedView.counts[region.key]}</strong>
                </span>
              ))}
            </div>
          </div>
          <div className='deployment-map-stage' style={{ borderColor: DEPLOYMENT_YEAR_COLORS[activeViewIndex], background: DEPLOYMENT_YEAR_BG_COLORS[activeViewIndex] }}>
            <DeploymentWorldMap className='deployment-map-svg' markers={markers} bgColor={DEPLOYMENT_YEAR_BG_COLORS[activeViewIndex]} />
          </div>
          <div className='deployment-year-legend'>
            {DEPLOYMENT_YEAR_VIEWS.slice(0, activeViewIndex + 1).map((view) => (
              <span key={view.label} className='deployment-year-legend-item'>
                <span className='deployment-year-legend-swatch' style={{ background: view.color }} />
                <span>{view.label}</span>
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function PartnerMarqueeSection({ section, collections }) {
  const PARTNER_BLOCKLIST = ['ge', 'general-electric', 'rolls-royce', 'diversion'];
  const items = (collections[section.collection] || []).filter(
    (item) => item.approvedForPublic !== false && !PARTNER_BLOCKLIST.includes((item.slug || '').toLowerCase()),
  );

  if (!items.length) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section partner-marquee-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <SectionHeader eyebrow={section.eyebrow} title={section.title} className='section-header compact' />
      <div className='partner-logo-grid'>
        {items.map((item, index) => (
          <a
            key={item.slug || item.name}
            className='partner-logo-item reveal-item'
            style={revealDelayStyle(index, 70)}
            href={item.href || undefined}
            target={item.href ? '_blank' : undefined}
            rel={item.href ? 'noreferrer' : undefined}
          >
            <OptimizedImage src={item.logo || item.image} alt={item.name} className='partner-logo-mark' fit='contain' sizes='220px' />
          </a>
        ))}
      </div>
    </ScrollReveal>
  );
}

function TeamPreview({ member, style }) {
  return (
    <article className='profile-card reveal-item' style={style}>
      <div className='profile-image-shell'>
        <OptimizedImage src={member.photo} alt={member.name} className='profile-image' fit='contain' sizes='(min-width: 1100px) 25vw, 100vw' />
      </div>
      <div className='profile-copy'>
        {member.role ? <p className='card-eyebrow'>{member.role}</p> : null}
        <h3>{member.name}</h3>
        <p>{member.bio}</p>
        {member.focus ? <span className='profile-focus'>{member.focus}</span> : null}
        {member.credentials?.length ? (
          <div className='profile-credentials' aria-label={`${member.name} credentials`}>
            {member.credentials.map((credential) => (
              <TeamCredential key={`${member.slug}-${credential.name}`} credential={credential} />
            ))}
          </div>
        ) : null}
        {member.linkedInUrl ? (
          <a href={member.linkedInUrl} target='_blank' rel='noreferrer' className='inline-link'>
            View LinkedIn
          </a>
        ) : null}
      </div>
    </article>
  );
}

function ResourceArticlePreview({ article, style }) {
  const articleMeta = formatArticleMeta(article);

  return (
    <article className='collection-card resource-article-card reveal-item' style={style}>
      {article.featuredImage ? (
        <div className='resource-article-image-shell'>
          <OptimizedImage src={article.featuredImage} alt={article.title} className='collection-card-image resource-article-image' />
        </div>
      ) : null}
      <div className='resource-article-copy'>
        {articleMeta ? <p className='card-eyebrow resource-article-meta'>{articleMeta}</p> : null}
        <h3>{article.title}</h3>
        {article.summary ? <p className='resource-article-summary'>{article.summary}</p> : null}
        <Link className='inline-link' to={`/resources/${article.slug}`}>
          Read article
        </Link>
      </div>
    </article>
  );
}

function getVisibleCollectionItems(section, collections) {
  const items = collections[section.collection] || [];

  return section.limit ? items.slice(0, section.limit) : items;
}

function LatestArticlesCarousel({ section, articles, id, className = '', isLoading, hasMore, onLoadMore }) {
  const carouselRef = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();
  const [scrollState, setScrollState] = useState({ canScrollPrev: false, canScrollNext: false });

  useEffect(() => {
    const node = carouselRef.current;

    if (!node || typeof window === 'undefined') {
      return undefined;
    }

    const updateScrollState = () => {
      setScrollState({
        canScrollPrev: node.scrollLeft > 1,
        canScrollNext: node.scrollLeft + node.clientWidth < node.scrollWidth - 1,
      });
    };

    updateScrollState();
    node.addEventListener('scroll', updateScrollState, { passive: true });
    window.addEventListener('resize', updateScrollState);

    return () => {
      node.removeEventListener('scroll', updateScrollState);
      window.removeEventListener('resize', updateScrollState);
    };
  }, [articles.length]);

  const scrollCarousel = (direction) => {
    const node = carouselRef.current;

    if (!node) {
      return;
    }

    // If scrolling forward and near the end, load more articles
    if (direction === 1 && !scrollState.canScrollNext && hasMore && onLoadMore) {
      onLoadMore();
      return;
    }

    const firstCard = node.querySelector('.resource-article-card');
    const computedStyle = window.getComputedStyle(node);
    const gap = Number.parseFloat(computedStyle.columnGap || computedStyle.gap) || 16;
    const distance = firstCard ? firstCard.getBoundingClientRect().width + gap : node.clientWidth * 0.85;

    node.scrollBy({
      left: direction * distance,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  };

  return (
    <div id={id || undefined} className={`latest-carousel ${className}`.trim()}>
      <div className='latest-carousel-header reveal-item'>
        <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} className='compact latest-carousel-title' />
        <div className='latest-carousel-actions'>
          {section.action ? (
            <Link className='inline-link latest-carousel-link' to={normalizeSiteHref(section.action.href)}>
              {section.action.label}
            </Link>
          ) : null}
          <div className='latest-carousel-controls' aria-label='Latest articles carousel controls'>
            <button
              type='button'
              className='latest-carousel-arrow latest-carousel-arrow-prev'
              onClick={() => scrollCarousel(-1)}
              disabled={!scrollState.canScrollPrev}
              aria-label='Show previous latest articles'
            />
            <button
              type='button'
              className='latest-carousel-arrow latest-carousel-arrow-next'
              onClick={() => scrollCarousel(1)}
              disabled={!scrollState.canScrollNext && !hasMore}
              aria-label='Show next latest articles'
            />
          </div>
        </div>
      </div>
      <div className='latest-carousel-viewport'>
        <div className='latest-carousel-track' ref={carouselRef} aria-label='Latest articles'>
          {articles.map((article, index) => (
            <ResourceArticlePreview key={article.slug} article={article} style={revealDelayStyle(index, 40, 80)} />
          ))}
        </div>
      </div>
    </div>
  );
}

function CollectionPreviewSection({ section, collections }) {
  const isLatestResourcesSection = section.collection === 'resourceArticles' && section.anchorId === LATEST_SECTION_ID;
  const { items: lazyItems, isLoading, hasMore, loadMore } = useContentfulCollection(
    section.collection,
    { pageSize: 6, immediate: isLatestResourcesSection }
  );

  const visibleItems = isLatestResourcesSection
    ? lazyItems
    : getVisibleCollectionItems(section, collections);
  const isTeamMembersSection = section.collection === 'teamMembers';
  const sectionStyle = undefined;

  if (!visibleItems.length && !isLoading) {
    return null;
  }

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      style={sectionStyle}
      className={`content-section collection-preview-section ${section.theme ? `theme-${section.theme}` : ''} ${isLatestResourcesSection ? 'latest-articles-section' : ''}`.trim()}
    >
      {isLatestResourcesSection ? (
        <LatestArticlesCarousel section={section} articles={visibleItems} isLoading={isLoading} hasMore={hasMore} onLoadMore={loadMore} />
      ) : (
        <>
          <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
          <div className={`collection-grid ${isTeamMembersSection ? 'team-preview-grid' : ''}`.trim()}>
            {visibleItems.map((item, index) => {
              const delayStyle = revealDelayStyle(index);

              if (section.collection === 'teamMembers') {
                return <TeamPreview key={item.slug} member={item} style={delayStyle} />;
              }

              if (section.collection === 'resourceArticles') {
                return <ResourceArticlePreview key={item.slug} article={item} style={delayStyle} />;
              }

              const href =
                section.collection === 'caseStudies'
                  ? `/case-studies/${item.slug}`
                  : section.collection === 'resourceArticles'
                    ? `/resources/${item.slug}`
                    : '#';

              return (
                <article key={item.slug} className='collection-card reveal-item' style={delayStyle}>
                  {item.heroImage || item.featuredImage ? (
                    <OptimizedImage
                      src={item.heroImage || item.featuredImage}
                      alt={item.title}
                      className='collection-card-image'
                    />
                  ) : null}
                  {item.category || item.sector ? <p className='card-eyebrow'>{item.category || item.sector}</p> : null}
                  <h3>{item.title}</h3>
                  <p>{item.summary}</p>
                  {href !== '#' ? (
                    <Link className='inline-link' to={href}>
                      Read more
                    </Link>
                  ) : null}
                </article>
              );
            })}
          </div>
        </>
      )}
      {section.action && !isLatestResourcesSection ? (
        <div className='section-footer-action reveal-item' style={revealDelayStyle(visibleItems.length, 70, 120)}>
          <Link className='secondary-cta' to={normalizeSiteHref(section.action.href)}>
            {section.action.label}
          </Link>
        </div>
      ) : null}
    </ScrollReveal>
  );
}

function TestimonialsSection({ collections }) {
  const testimonials = collections?.testimonials || [];

  if (!testimonials.length) {
    return null;
  }

  return (
    <ScrollReveal as='section' className='content-section testimonials-section'>
      <div className='testimonials-list'>
        {testimonials.map((item, index) => (
          <blockquote key={item.id || item.companyName} className='testimonial-item reveal-item' style={revealDelayStyle(index, 60, 100)}>
            {item.happyClientImage ? (
              <OptimizedImage
                src={item.happyClientImage}
                alt={item.customerName}
                className='testimonial-client-image'
              />
            ) : null}
            <p className='testimonial-body'>&ldquo;{item.testimonial}&rdquo;</p>
            <footer className='testimonial-attribution'>
              <cite className='testimonial-name'>{item.customerName}</cite>
              {item.companyName ? <span className='testimonial-company'>{item.companyName}</span> : null}
            </footer>
          </blockquote>
        ))}
      </div>
    </ScrollReveal>
  );
}

function SplitListSection({ section }) {
  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section split-list-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
      <div className='split-list-grid'>
        {section.columns.map((column, index) => (
          <article key={column.title} className='split-list-card reveal-item' style={revealDelayStyle(index, 100)}>
            <h3>{column.title}</h3>
            <ul>
              {column.items.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </article>
        ))}
      </div>
    </ScrollReveal>
  );
}

const MARKET_SECTORS = [
  { label: 'Aerospace', value: 19.71 },
  { label: 'Defence', value: 18.59 },
  { label: 'Automotive', value: 10.7 },
  { label: 'Energy', value: 9.57 },
  { label: 'Medical', value: 8.45 },
  { label: 'Space', value: 7.32 },
  { label: 'Tooling', value: 5.07 },
  { label: 'Automation', value: 5.07 },
  { label: 'Electronics', value: 3.66 },
  { label: 'Naval', value: 3.66 },
  { label: 'Consumer', value: 3.66 },
  { label: 'Rail', value: 3.38 },
  { label: 'Construction', value: 0.84 },
];

const MARKET_APPLICATIONS = [
  { label: '1000mm PBF', value: 50 },
  { label: '2000mm Casting', value: 12 },
  { label: '2000mm PBF', value: 11.5 },
  { label: 'Binder Jet', value: 6 },
  { label: 'WAAM', value: 4.5 },
  { label: 'MRO', value: 3.5 },
  { label: 'Weld Bead Cleanup', value: 3 },
  { label: 'Ceramics', value: 3 },
  { label: 'Polymer', value: 3 },
  { label: 'Cold Spray', value: 3 },
];

function MarketBar({ label, value, max }) {
  return (
    <div className='market-bar'>
      <div className='market-bar-label'>{label}</div>
      <div className='market-bar-track'>
        <div className='market-bar-fill' style={{ width: `${(value / max) * 100}%` }} />
      </div>
      <div className='market-bar-value'>{value}%</div>
    </div>
  );
}

function MarketTractionSection({ section }) {
  const isMobileView = typeof window !== 'undefined' && window.innerWidth <= 860;
  const { register, passed } = useScrollTracker();
  const [timelineComplete, setTimelineComplete] = useState(false);
  const sectionRef = useCallback((el) => register('marketTraction', el), [register]);

  useEffect(() => {
    if (isMobileView) return;
    const handler = () => setTimelineComplete(true);
    window.addEventListener('timeline-complete', handler);
    return () => window.removeEventListener('timeline-complete', handler);
  }, [isMobileView]);

  const hasRevealed = useRef(false);
  if (!hasRevealed.current && (isMobileView || (timelineComplete && passed.team))) {
    hasRevealed.current = true;
  }
  const revealed = hasRevealed.current;

  const tabs = ['sectors', 'applications'];
  const wrapperRef = useRef(null);
  const [activeIndex, setActiveIndex] = useScrollProgress(wrapperRef, tabs.length, isMobileView ? 0.1 : 0.4);
  const tab = tabs[activeIndex];
  const data = tab === 'sectors' ? MARKET_SECTORS : MARKET_APPLICATIONS;
  const max = data[0].value;

  const content = (
    <ScrollReveal as='section' className='content-section'>
      <div className='market-traction-scroll-wrapper' ref={wrapperRef}>
        <div className='market-traction-sticky'>
          <SectionHeader eyebrow={section.eyebrow} title={section.title} />
          <div className='market-tabs'>
            <button className={`market-tab ${tab === 'sectors' ? 'is-active' : ''}`} onClick={() => setActiveIndex(0)}>Sectors</button>
            <button className={`market-tab ${tab === 'applications' ? 'is-active' : ''}`} onClick={() => setActiveIndex(1)}>Applications</button>
          </div>
          <div className='market-bars'>
            {data.map((item) => (
              <MarketBar key={item.label} label={item.label} value={item.value} max={max} />
            ))}
          </div>
          <p className='market-insight'>
            {tab === 'sectors'
              ? 'Aerospace and defence lead the sector picture. Automotive, energy, medical, and space add meaningful spread.'
              : 'Large-format PBF is the strongest signal. 1000mm PBF alone represents half of the market data.'}
          </p>
        </div>
      </div>
    </ScrollReveal>
  );

  return <div ref={sectionRef}>{content}</div>;
}

function TimelineSection({ section }) {
  const items = section.items || [];
  const [lightbox, setLightbox] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  // Flatten items + substeps into scroll steps
  const steps = [];
  items.forEach((item) => {
    steps.push(item);
    if (item.substeps) {
      item.substeps.forEach((sub) => steps.push({ ...sub, _parentLabel: item.label }));
    }
  });

  const wrapperRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const activeStepRef = useRef(0);
  const cooldownRef = useRef(false);
  const cooldownTimerRef = useRef(null);
  const [showScrollHint, setShowScrollHint] = useState(true);
  const [shakeHint, setShakeHint] = useState(false);
  const lockedRef = useRef(false);
  const completedRef = useRef(false);
  const STEP_DELAY = 700;

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 860px)');
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  const advanceStep = useCallback((dir) => {
    if (cooldownRef.current) return;
    const next = Math.max(0, Math.min(steps.length - 1, activeStepRef.current + dir));
    if (next === activeStepRef.current) return;
    activeStepRef.current = next;
    setActiveStep(next);
    if (next >= steps.length - 1) {
      completedRef.current = true;
      window.dispatchEvent(new Event('timeline-complete'));
      setShakeHint(true);
      setTimeout(() => setShakeHint(false), 800);
    }
    cooldownRef.current = true;
    clearTimeout(cooldownTimerRef.current);
    cooldownTimerRef.current = setTimeout(() => { cooldownRef.current = false; }, STEP_DELAY);
  }, [steps.length]);

  const jumpToStep = useCallback((i) => {
    clearTimeout(cooldownTimerRef.current);
    cooldownRef.current = false;
    activeStepRef.current = i;
    setActiveStep(i);
    // Any dot click bypasses scroll-driven navigation — mark complete and unlock
    // so the IntersectionObserver never re-locks scroll when the user scrolls back up
    if (lockedRef.current) {
      lockedRef.current = false;
      document.body.style.overflow = '';
    }
    completedRef.current = true;
    setShowScrollHint(false);
    window.dispatchEvent(new Event('timeline-complete'));
  }, []);

  // Lock/unlock body scroll
  const lockScroll = useCallback(() => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    document.body.style.overflow = 'hidden';
  }, []);

  const unlockScroll = useCallback(() => {
    if (!lockedRef.current) return;
    lockedRef.current = false;
    document.body.style.overflow = '';
  }, []);

  // IntersectionObserver to detect when timeline enters viewport and lock scroll
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || isMobile) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !completedRef.current && activeStepRef.current < steps.length - 1) {
          // Only lock if the user has scrolled the timeline near the top of the viewport
          const rect = wrapper.getBoundingClientRect();
          if (rect.top < window.innerHeight * 0.4) {
            lockScroll();
          }
        } else {
          unlockScroll();
        }
      },
      { threshold: 0.5 }
    );

    observer.observe(wrapper);
    return () => { observer.disconnect(); unlockScroll(); };
  }, [steps.length, isMobile, lockScroll, unlockScroll]);

  // Wheel/touch/key handlers — fixed speed, one step per event
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || isMobile) return;
    let touchStartY = 0;

    function handleWheel(e) {
      if (!lockedRef.current) return;
      e.preventDefault();
      if (Math.abs(e.deltaY) < 30) return;
      const dir = e.deltaY > 0 ? 1 : -1;
      if (dir === 1) {
        advanceStep(1);
        if (activeStepRef.current >= steps.length - 1) {
          setTimeout(unlockScroll, STEP_DELAY);
        }
      } else {
        advanceStep(-1);
      }
    }

    function handleTouchStart(e) {
      touchStartY = e.touches[0].clientY;
    }

    function handleTouchMove(e) {
      if (!lockedRef.current) return;
      e.preventDefault();
      const dy = touchStartY - e.touches[0].clientY;
      if (Math.abs(dy) < 20) return;
      touchStartY = e.touches[0].clientY;
      const dir = dy > 0 ? 1 : -1;
      if (dir === 1) {
        advanceStep(1);
        if (activeStepRef.current >= steps.length - 1) {
          setTimeout(unlockScroll, STEP_DELAY);
        }
      } else {
        advanceStep(-1);
      }
    }

    function handleKeyDown(e) {
      if (!lockedRef.current) return;
      if (e.key === 'ArrowDown' || e.key === 'PageDown' || e.key === ' ') {
        e.preventDefault();
        advanceStep(1);
        if (activeStepRef.current >= steps.length - 1) {
          setTimeout(unlockScroll, STEP_DELAY);
        }
      }
      if (e.key === 'ArrowUp' || e.key === 'PageUp') {
        e.preventDefault();
        advanceStep(-1);
      }
    }

    document.addEventListener('wheel', handleWheel, { passive: false, capture: true });
    document.addEventListener('touchstart', handleTouchStart, { passive: true, capture: true });
    document.addEventListener('touchmove', handleTouchMove, { passive: false, capture: true });
    document.addEventListener('keydown', handleKeyDown, { capture: true });

    return () => {
      document.removeEventListener('wheel', handleWheel, { capture: true });
      document.removeEventListener('touchstart', handleTouchStart, { capture: true });
      document.removeEventListener('touchmove', handleTouchMove, { capture: true });
      document.removeEventListener('keydown', handleKeyDown, { capture: true });
      clearTimeout(cooldownTimerRef.current);
    };
  }, [steps.length, advanceStep, isMobile, unlockScroll]);

  const currentStep = steps[activeStep] || steps[0];

  // Find which item index the current step belongs to (for the dot highlight)
  let activeItemIndex = 0;
  let count = 0;
  for (let i = 0; i < items.length; i++) {
    const total = 1 + (items[i].substeps?.length || 0);
    if (activeStep < count + total) { activeItemIndex = i; break; }
    count += total;
  }

  const handleExit = () => {
    completedRef.current = true;
    unlockScroll();
    setShowScrollHint(false);
    window.dispatchEvent(new Event('timeline-complete'));
    const section = wrapperRef.current?.closest('.timeline-section');
    const next = section?.nextElementSibling;
    if (next) setTimeout(() => next.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section timeline-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <div className='timeline-scroll-wrapper' ref={wrapperRef} style={{ '--step-count': steps.length }}>
        <div className='timeline-sticky'>
          <div className='timeline-header-row'>
            <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
            {items[0]?.image ? (
              <div className='timeline-header-media'>
                <img
                  src={items[0].image}
                  alt={section.title}
                  className='timeline-header-image'
                  style={{ opacity: currentStep.image ? 1 : 0 }}
                  onClick={() => currentStep.image && setLightbox(items[0].image)}
                />
                {currentStep.coordinates ? (
                  <span className='timeline-coordinates'>{currentStep.coordinates}</span>
                ) : null}
              </div>
            ) : null}
          </div>
          {lightbox ? (
            <div className='timeline-image-lightbox' onClick={() => setLightbox(null)}>
              <img src={lightbox} alt={section.title} />
            </div>
          ) : null}
          {isMobile ? (
            <div className='timeline-mobile-nav'>
              <button className='timeline-nav-arrow' disabled={activeStep === 0} onClick={() => advanceStep(-1)} aria-label='Previous step'>&#8592;</button>
              <span className='timeline-nav-counter'>{activeStep + 1} / {steps.length}</span>
              <button className='timeline-nav-arrow' disabled={activeStep >= steps.length - 1} onClick={() => advanceStep(1)} aria-label='Next step'>&#8594;</button>
            </div>
          ) : null}
          <div className='interactive-timeline'>
            <div className='timeline-track'>
              <div className='timeline-line' />
              <div className='timeline-progress' style={{ width: `${(activeItemIndex / Math.max(items.length - 1, 1)) * 100}%` }} />
              {items.map((item, index) => (
                <button
                  key={`${item.label}-${index}`}
                  className={`timeline-dot ${index === activeItemIndex ? 'is-active' : ''} ${index <= activeItemIndex ? 'is-past' : ''}`}
                  style={{ left: `${(index / (items.length - 1)) * 100}%` }}
                  onClick={() => {
                    let stepIdx = 0;
                    for (let i = 0; i < index; i++) stepIdx += 1 + (items[i].substeps?.length || 0);
                    jumpToStep(stepIdx);
                  }}
                  aria-label={item.label}
                >
                  <span className='timeline-year'>{item.label}</span>
                </button>
              ))}
            </div>
            <div className='timeline-content'>
              <h3 className='timeline-content-title'>{currentStep.title}</h3>
              <p className='timeline-content-body'>{currentStep.body}</p>
              {currentStep.highlights?.length ? (
                <div className='timeline-highlights'>
                  {['customer', 'investor', 'certification', 'milestone'].map((type) => {
                    const group = currentStep.highlights.filter((h) => h.type === type);
                    if (!group.length) return null;
                    return (
                      <div key={type} className='timeline-highlight-group'>
                        <span className='timeline-highlight-type'>{type}s</span>
                        <div className='timeline-highlight-logos'>
                          {group.map((h, i) => {
                            const logoKey = h.logoKey?.toLowerCase();
                            const resolvedLogo = h.logo || (!h.text && logoKey && HERO_COMPANY_LOGOS.find((l) => {
                              const file = l.file?.toLowerCase() || '';
                              const name = l.name?.toLowerCase() || '';
                              const text = l.text?.toLowerCase() || '';
                              return file.includes(logoKey) || name.includes(logoKey) || text.includes(logoKey);
                            }));
                            return (
                              <span key={i} className='timeline-highlight-item'>
                                {h.text ? (
                                  <span className='timeline-highlight-name timeline-highlight-name-badge'>{h.text}</span>
                                ) : resolvedLogo?.text ? (
                                  <span className='timeline-highlight-name timeline-highlight-name-badge'>{resolvedLogo.text}</span>
                                ) : resolvedLogo?.file ? (
                                  <img
                                    src={buildCompanyLogoSrc(resolvedLogo.file)}
                                    alt={h.name}
                                    className='timeline-highlight-logo'
                                  />
                                ) : h.logo ? (
                                  <img src={h.logo} alt={h.name} className='timeline-highlight-logo' />
                                ) : (
                                  <span className='timeline-highlight-name'>{h.name}</span>
                                )}
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : null}
              {currentStep.awards?.length ? (
                <div className='timeline-awards'>
                  <div className='timeline-award-images'>
                    {currentStep.awards.map((award, i) => (
                      <img key={i} src={award.image} alt={award.title} className='timeline-award-image' />
                    ))}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
          {showScrollHint && !isMobile ? (
            <button className={`timeline-scroll-hint is-visible${shakeHint ? ' is-shaking' : ''}`} onClick={handleExit} aria-label='Scroll to next section'>
              <span className='timeline-scroll-hint-text'>Scroll</span>
              <span className='timeline-scroll-hint-arrow' />
            </button>
          ) : null}
        </div>
      </div>
    </ScrollReveal>
  );
}

function DualVideoPlayer({ src, className = '' }) {
  const videoRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;
    if (!document.fullscreenElement) {
      el.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  };

  return (
    <div className={`dual-video-player${className ? ` ${className}` : ''}`}>
      <video
        ref={videoRef}
        src={src}
        autoPlay
        loop
        muted
        playsInline
        className='dual-video-media'
      />
      <button
        className='dual-video-fullscreen-btn'
        onClick={toggleFullscreen}
        aria-label={isFullscreen ? 'Exit full screen' : 'Enter full screen'}
      >
        {isFullscreen ? (
          <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M8 3v3a2 2 0 0 1-2 2H3'/><path d='M21 8h-3a2 2 0 0 1-2-2V3'/>
            <path d='M3 16h3a2 2 0 0 1 2 2v3'/><path d='M16 21v-3a2 2 0 0 1 2-2h3'/>
          </svg>
        ) : (
          <svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true'>
            <path d='M8 3H5a2 2 0 0 0-2 2v3'/><path d='M21 8V5a2 2 0 0 0-2-2h-3'/>
            <path d='M3 16v3a2 2 0 0 0 2 2h3'/><path d='M16 21h3a2 2 0 0 0 2-2v-3'/>
          </svg>
        )}
      </button>
    </div>
  );
}

function DualVideoSection({ collections }) {
  const entries = collections?.r1000HomeDualVideo || [];
  const secondEntry = entries.find((e) => e.panel === 'Second video');
  const src = secondEntry?.cdnVideoFileName
    ? `${HOME_MEDIA_CDN_BASE_URL}/${encodeURIComponent(secondEntry.cdnVideoFileName)}`
    : null;

  return (
    <section className='content-section dual-video-section dual-video-section--wide'>
      <div className='dual-video-wide'>
        {src ? <DualVideoPlayer src={src} /> : <div className='dual-video-placeholder dual-video-placeholder--wide' />}
      </div>
    </section>
  );
}

function CtaBannerSection({ section }) {
  if (section.variant === 'minimal') {
    return (
      <ScrollReveal as='section' className='content-section cta-banner-section-minimal'>
        <div className='hero-actions' style={{ justifyContent: 'center' }}>
          {section.primaryAction ? (
            <Link className='primary-cta' to={normalizeSiteHref(section.primaryAction.href)}>
              {section.primaryAction.label}
            </Link>
          ) : null}
        </div>
      </ScrollReveal>
    );
  }

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      threshold={0.2}
      className={`content-section cta-banner-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <div className='cta-banner reveal-item'>
        <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
        {section.points?.length ? (
          <ul className='cta-banner-points'>
            {section.points.map((point, i) => (
              <li key={i}>{point}</li>
            ))}
          </ul>
        ) : null}
        <div className='hero-actions cta-banner-actions'>
          {section.primaryAction ? (
            <Link className='primary-cta' to={normalizeSiteHref(section.primaryAction.href)}>
              {section.primaryAction.label}
            </Link>
          ) : null}
          {section.secondaryAction ? (
            <Link className='secondary-cta' to={normalizeSiteHref(section.secondaryAction.href)}>
              {section.secondaryAction.label}
            </Link>
          ) : null}
        </div>
      </div>
    </ScrollReveal>
  );
}

function PolicySection({ section }) {
  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section policy-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <div className='policy-grid'>
        {section.items.map((item, index) => (
          <article key={item.title} className='policy-card reveal-item' style={revealDelayStyle(index, 90)}>
            <h3>{item.title}</h3>
            <p>{item.body}</p>
          </article>
        ))}
      </div>
    </ScrollReveal>
  );
}

function TabbedVideoSection({ section }) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [activeTab, setActiveTab] = useState(0);
  const activeMedia = section.tabs?.[activeTab]?.media || section.media;
  const hasVideo = activeMedia?.type === 'video' && !prefersReducedMotion;

  return (
    <ScrollReveal
      as='section'
      id={section.anchorId || undefined}
      className={`content-section tabbed-video-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}
    >
      <div className='tabbed-video-copy reveal-item'>
        <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
        {section.tabs?.length ? (
          <div className='tabbed-video-tabs' role='tablist'>
            {section.tabs.map((tab, index) => (
              <button
                key={tab.label}
                type='button'
                role='tab'
                aria-selected={index === activeTab}
                className={`tabbed-video-tab ${index === activeTab ? 'active' : ''}`.trim()}
                onClick={() => setActiveTab(index)}
              >
                {tab.label}
              </button>
            ))}
          </div>
        ) : null}
        {section.actions?.length ? (
          <div className='hero-actions'>
            {section.actions.map((action, index) => (
              <Link key={action.href} className={index === 0 ? 'primary-cta' : 'secondary-cta'} to={normalizeSiteHref(action.href)}>
                {action.label}
              </Link>
            ))}
          </div>
        ) : null}
      </div>
      <div className='tabbed-video-media reveal-item' style={revealDelayStyle(0, 0, 140)}>
        <div className='hero-media-stage'>
          {hasVideo ? (
            <LazyVideo
              key={activeMedia?.src}
              className='hero-video-shell'
              videoClassName='hero-video'
              src={activeMedia?.src}
              poster={activeMedia?.poster}
            />
          ) : (
            <div className='hero-image-stage'>
              <OptimizedImage className='hero-image' src={activeMedia?.poster || activeMedia?.src} alt={activeMedia?.alt || section.title} />
            </div>
          )}
        </div>
      </div>
    </ScrollReveal>
  );
}

function SpecTableSection({ section, layoutImage, layoutContentType }) {
  const [activeTab, setActiveTab] = React.useState('specs');
  const isPdf = layoutContentType === 'application/pdf' || layoutImage?.endsWith('.pdf');

  return (
    <ScrollReveal as='section' id={section.anchorId || undefined} className={`content-section spec-table-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}>
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
      {section.layout && (
        <div className='spec-tabs'>
          <button className={`spec-tab${activeTab === 'specs' ? ' is-active' : ''}`} onClick={() => setActiveTab('specs')}>Specs</button>
          <button className={`spec-tab${activeTab === 'layout' ? ' is-active' : ''}`} onClick={() => setActiveTab('layout')}>Layout</button>
        </div>
      )}
      {activeTab === 'specs' && (
        <div className='spec-table'>
          {section.groups.map((group) => (
            <div key={group.label} className='spec-group reveal-item'>
              <p className='spec-group-label'>{group.label}</p>
              <dl className='spec-list'>
                {group.items.map((item) => (
                  <div key={item.key} className='spec-row'>
                    <dt className='spec-key'>{item.key}</dt>
                    <dd className='spec-value'>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
      {activeTab === 'layout' && section.layout && (
        <div className='spec-layout'>
          {layoutImage && (
            <div className='spec-layout-image'>
              {isPdf ? (
                <iframe src={layoutImage} title='r1000 layout schematic' width='100%' height='600' style={{ border: 'none' }} />
              ) : (
                <img src={layoutImage} alt='r1000 layout schematic' loading='lazy' />
              )}
            </div>
          )}
          <div className='spec-table'>
            <div className='spec-group'>
              <p className='spec-group-label'>Footprint &amp; weight</p>
              <dl className='spec-list'>
                {section.layout.items.map((item) => (
                  <div key={item.key} className='spec-row'>
                    <dt className='spec-key'>{item.key}</dt>
                    <dd className='spec-value'>{item.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      )}
    </ScrollReveal>
  );
}

function ModuleShowcaseSection({ section }) {
  const [active, setActive] = React.useState(0);
  const interacted = React.useRef(false);
  const modules = section.modules || [];
  const count = modules.length;
  const visibleCount = section.visibleCount || 1;

  React.useEffect(() => {
    if (count <= 1) return undefined;
    const id = setInterval(() => {
      if (visibleCount > 1 || !interacted.current) setActive((i) => (i + 1) % count);
    }, 3500);
    return () => clearInterval(id);
  }, [count, visibleCount]);

  if (!count) return null;

  if (visibleCount > 1) {
    return (
      <ScrollReveal as='section' id={section.anchorId || undefined} className={`content-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}>
        <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
        <div className='module-3d-carousel'>
          <div className='module-3d-stage'>
            {modules.map((mod, i) => {
              const offset = (i - active + count) % count;
              let posClass;
              if (offset === 0) posClass = 'is-center';
              else if (offset === 1) posClass = 'is-right';
              else if (offset === count - 1) posClass = 'is-left';
              else posClass = 'is-hidden';
              return (
                <div key={mod.label} className={`module-3d-item ${posClass}`}>
                  {mod.image && <img src={mod.image} alt={mod.alt || mod.label} loading='lazy' />}
                  <p className='module-carousel-label'>{mod.label}</p>
                  {mod.body && <p className='module-carousel-body'>{mod.body}</p>}
                </div>
              );
            })}
          </div>
          {count > 1 && (
            <div className='module-carousel-dots'>
              {modules.map((m, i) => (
                <button type='button' key={m.label} className={`module-carousel-dot${i === active ? ' is-active' : ''}`} onClick={() => setActive(i)} aria-label={m.label} />
              ))}
            </div>
          )}
        </div>
      </ScrollReveal>
    );
  }

  const prev = () => { interacted.current = true; setActive((i) => (i - 1 + count) % count); };
  const next = () => { interacted.current = true; setActive((i) => (i + 1) % count); };
  const mod = modules[active];

  return (
    <ScrollReveal as='section' id={section.anchorId || undefined} className={`content-section ${section.theme ? `theme-${section.theme}` : ''}`.trim()}>
      <SectionHeader eyebrow={section.eyebrow} title={section.title} body={section.body} />
      <div className='module-carousel'>
        <div className='module-carousel-stage'>
          <button type='button' className='module-carousel-btn' onClick={prev} aria-label='Previous module'>&#8592;</button>
          <div className='module-carousel-visual is-loaded' style={{ position: 'relative' }}>
            {modules.map((m, i) => (
              <img key={m.label} src={m.image} alt={m.alt} loading='eager' style={{ position: i === active ? 'relative' : 'absolute', top: 0, left: 0, width: '100%', opacity: i === active ? 1 : 0, transition: 'opacity 0.4s ease' }} />
            ))}
          </div>
          <button type='button' className='module-carousel-btn' onClick={next} aria-label='Next module'>&#8594;</button>
        </div>
        <div className='module-carousel-meta'>
          <p className='module-carousel-label'>{mod.label}</p>
          <p className='module-carousel-body'>{mod.body}</p>
        </div>
        <div className='module-carousel-dots'>
          {modules.map((m, i) => (
            <button
              type='button'
              key={m.label}
              className={`module-carousel-dot${i === active ? ' is-active' : ''}`}
              onClick={() => { interacted.current = true; setActive(i); }}
              aria-label={m.label}
            />
          ))}
        </div>
      </div>
    </ScrollReveal>
  );
}

function TrackedDeploymentMap() {
  return <DeploymentMapSection />;
}

function renderSection(section, collections) {
  switch (section.type) {
    case 'deploymentMap':
      return <TrackedDeploymentMap />;
    case 'specTable': {
      const layoutImg = collections?.layoutImages?.find((i) => i.label === 'r1000' || i.label === '' || i.label?.includes('r1000')) || collections?.layoutImages?.[0];
      return <SpecTableSection section={section} layoutImage={layoutImg?.image} layoutContentType={layoutImg?.contentType} />;
    }
    case 'moduleShowcase':
      return <ModuleShowcaseSection section={section} />;
    case 'partnerMarquee':
      return <PartnerMarqueeSection section={section} collections={collections} />;
    case 'logoStrip':
      return <LogoStripSection section={section} />;
    case 'cards':
      return <CardsSection section={section} />;
    case 'tabbedVideo':
      return <TabbedVideoSection section={section} />;
    case 'mediaSpotlight':
      return <MediaSpotlightSection section={section} />;
    case 'renderShowcase':
      return <RenderShowcaseSection section={section} />;
    case 'productChooser':
      return <ProductChooserSection section={section} />;
    case 'statBand':
      return <StatBandSection section={section} collections={collections} />;
    case 'collectionPreview':
      return <CollectionPreviewSection section={section} collections={collections} />;
    case 'testimonials':
      return <TestimonialsSection collections={collections} />;
    case 'splitList':
      return <SplitListSection section={section} />;
    case 'timeline':
      return <TimelineSection section={section} />;
    case 'marketTraction':
      return <MarketTractionSection section={section} />;
    case 'ctaBanner':
      return <CtaBannerSection section={section} />;
    case 'dualVideo':
      return <DualVideoSection collections={collections} />;
    case 'scrollPrompt':
      return (
        <div className='scroll-prompt' aria-hidden='true'>
          <span className='scroll-prompt-text'>scroll</span>
          <span className='scroll-prompt-arrow' />
        </div>
      );
    case 'process':
      return <ProcessSection section={section} />;
    case 'policy':
      return <PolicySection section={section} />;
    default:
      return null;
  }
}

function applyHeroVideosToCapabilityReel(capabilityReel, heroVideos, cdnBaseUrl) {
  if (!heroVideos?.length || !capabilityReel?.length) {
    return capabilityReel;
  }

  return capabilityReel.map((item) => {
    const match = heroVideos.find(
      (v) => v.heroSection && v.heroSection.toLowerCase() === (item.eyebrow || '').toLowerCase(),
    );
    if (!match || !match.cdnVideoFileName) {
      return item;
    }
    const videoSrc = `${cdnBaseUrl}/${encodeURIComponent(match.cdnVideoFileName)}`;
    return {
      ...item,
      duration: match.videoRuntime * 1000,
      media: {
        ...item.media,
        type: 'video',
        src: videoSrc,
      },
    };
  });
}

function applyHomeDualVideos(sections, r1000HomeDualVideo, cdnBaseUrl) {
  const firstEntry = (r1000HomeDualVideo || []).find((e) => e.panel === 'First video');
  const videoSrc = firstEntry?.cdnVideoFileName
    ? `${cdnBaseUrl}/${encodeURIComponent(firstEntry.cdnVideoFileName)}`
    : null;
  return sections.map((section) => {
    if (section.type !== 'renderShowcase' || section.anchorId !== 'meet-r-1000') return section;
    return {
      ...section,
      media: videoSrc ? { type: 'video', src: videoSrc } : { type: 'videoPlaceholder' },
    };
  });
}

function applyR1000ProductVideos(sections, r1000ProductVideos, cdnBaseUrl) {
  if (!r1000ProductVideos?.length) {
    return sections;
  }

  return sections.map((section) => {
    if (section.type !== 'tabbedVideo' || !section.tabs?.length) {
      return section;
    }

    const updatedTabs = section.tabs.map((tab) => {
      const match = r1000ProductVideos.find(
        (v) => v.productVideoTab && v.productVideoTab.toLowerCase() === (tab.label || '').toLowerCase(),
      );
      if (!match || !match.cdnVideoFileName) {
        return tab;
      }
      const videoSrc = `${cdnBaseUrl}/${encodeURIComponent(match.cdnVideoFileName)}`;
      return {
        ...tab,
        media: {
          ...tab.media,
          type: 'video',
          src: videoSrc,
        },
      };
    });

    return { ...section, tabs: updatedTabs };
  });
}

export function PageRenderer({ page }) {
  const { siteContent } = useSiteContent();
  const isProductPage = page?.path?.startsWith('/products') || page?.path?.startsWith('/solutions');
  const isHomePage = page?.path === '/';
  // Lazy-load video collections for home/product pages
  useContentfulCollection('heroVideos', { pageSize: 20, immediate: isHomePage });
  useContentfulCollection('r1000ProductVideos', { pageSize: 20, immediate: isProductPage });
  // Lazy-load product page assets
  useContentfulAssets('toolBitOptions/', 'toolBitOptions', { immediate: isProductPage });
  useContentfulAssets('toolOptions/', 'toolOptions', { immediate: isProductPage });
  useContentfulAssets('AccessoryOptions/', 'accessoryOptions', { immediate: isProductPage });
  useContentfulAssets('layout/', 'layoutImages', { immediate: isProductPage });
  useContentfulCollection('r1000HomeDualVideo', { pageSize: 10, immediate: isHomePage });
  const normalizedHero = normalizePageHero(page);
  const normalizedSections = normalizePageSections(page);
  const visibleSections =
    page.path === '/' && normalizedHero?.partnerLogosInHero
      ? normalizedSections.filter((section) => section.type !== 'partnerMarquee')
      : normalizedSections.map((section) => {
          if (section.type !== 'moduleShowcase') return section;
          const eyebrow = (section.eyebrow || '').toLowerCase();
          if (eyebrow === 'toolbit options' && siteContent.collections.toolBitOptions?.length) {
            return { ...section, modules: siteContent.collections.toolBitOptions };
          }
          if (eyebrow === 'tool options' && siteContent.collections.toolOptions?.length) {
            return { ...section, modules: siteContent.collections.toolOptions };
          }
          if (eyebrow === 'accessory options' && siteContent.collections.accessoryOptions?.length) {
            return { ...section, modules: siteContent.collections.accessoryOptions };
          }
          return section;
        });

  const sectionsWithHomeDualVideos = applyHomeDualVideos(
    visibleSections,
    siteContent.collections.r1000HomeDualVideo,
    HOME_MEDIA_CDN_BASE_URL,
  );

  const sectionsWithProductVideos = applyR1000ProductVideos(
    sectionsWithHomeDualVideos,
    siteContent.collections.r1000ProductVideos,
    HOME_MEDIA_CDN_BASE_URL,
  );

  const heroWithVideos = normalizedHero?.capabilityReel?.length
    ? {
        ...normalizedHero,
        capabilityReel: applyHeroVideosToCapabilityReel(
          normalizedHero.capabilityReel,
          siteContent.collections.heroVideos,
          HOME_MEDIA_CDN_BASE_URL,
        ),
      }
    : normalizedHero;

  return (
    <ScrollTrackerProvider>
    <div className='page-shell'>
      <Hero
        hero={heroWithVideos}
        isHomePage={page.path === '/'}
      />
      {normalizedHero?.variant === 'cinematicMinimal' && normalizedHero?.stats?.length ? (
        <HeroMetrics stats={normalizedHero.stats} />
      ) : null}
      {sectionsWithProductVideos.map((section, index) => (
        <React.Fragment key={`${section.type}-${section.title || index}`}>
          {renderSection(section, siteContent.collections)}
        </React.Fragment>
      ))}
    </div>
    </ScrollTrackerProvider>
  );
}
